import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { Check, Copy } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { api, ApiError } from "@/lib/api";
import { useAuthToken } from "@/lib/auth";
import { onDashboardChanged } from "@/lib/dashboardWs";
import { toast } from "@/components/ui/toast";
import {
  attemptModeLabel,
  canDeprecate,
  canRebenchMacos,
  canRollBack,
  confirmText,
  rebenchConfirmText,
  deprecationChanges,
  deriveVersionState,
  groupByVersion,
  primaryAction,
  queuedToast,
  servesBuild,
  versionKey,
  withLocalQueued,
  LOCAL_QUEUED_TTL_MS,
  type LocalQueuedRequest,
  type PipelineActionKind,
  type PrimaryAction,
  type VersionState,
} from "@/lib/versionState";
import {
  fetchRegistryBuilds,
  registryPublicUrl,
  type RegistryBuildsView,
} from "@/lib/registryBuilds";
import { VersionBuilds } from "@/components/registry/VersionBuilds";
import { DeleteBuildsDialog } from "@/components/registry/DeleteBuildsDialog";
import { selectWithdrawn, toggleSelected } from "@/lib/buildDeletion";
import type { DownloadsReport, DownloadsState } from "@/lib/downloads";
import { AttemptBuilds } from "@/components/registry/AttemptBuilds";
import { BenchDiagnostics } from "@/components/registry/BenchDiagnostics";
import { parseCompileJobs, sequenceText } from "@/lib/buildLabels";
import {
  macosBenchState,
  macosState,
  macosVerifyState,
} from "@/lib/nativeTargets";
import {
  attemptsWithProblems,
  benchmarkBanner,
  benchmarkStage,
  parseSteps,
  publishedLabel,
  totalElapsedMs,
} from "@/lib/stepChecklist";
import { pgoHeadline, pgoLayers } from "@/lib/pgoLayers";
import {
  driftHint,
  parseRunSettings,
  parseTestsAfterPublish,
  runSettingsLine,
  settingChange,
  settingsDrift,
  testsAfterPublishBadge,
  TOGGLE_TITLE,
  type SettingKey,
  type SettingOverride,
  type SettingsView,
} from "@/lib/publishToggles";
import { PublishToggles } from "@/components/registry/PublishToggles";
import { fmtDurationMs } from "@/lib/buildLabels";
import { StepProblemBanner } from "@/components/registry/StepChecklist";
import { PublishSteps } from "@/components/registry/PublishSteps";
import { AttemptSummaryCard } from "@/components/registry/AttemptSummaryCard";
import { CancelAttemptButton } from "@/components/registry/CancelAttemptButton";
import { Ago, UpdatedAgo } from "@/components/registry/LiveTime";
import {
  createCell,
  quietly,
  shareMap,
  shareStructure,
  stableOrder,
} from "@/lib/quietRefresh";
import { useStepSelection } from "@/components/registry/useStepSelection";
import { Drawer } from "@/components/ui/drawer";
import { attemptSummary } from "@/lib/attemptSummary";
import { cn } from "@/lib/utils";

/**
 * Registry — the operator-facing view of internal/publish's compile-
 * test-publish pipeline (internal/publish/plans/2026-09-08-publish-
 * pipeline.md §7.1). `DependenciesPage.tsx` is the structural/stylistic
 * template: cards wrapping a `Table`, per-row actions, a page-level
 * action button, data via the shared `api()` wrapper, live-refresh via
 * `onDashboardChanged`.
 *
 * UX rework, 2026-09-16, direct operator request ("I want to have an
 * amazing management experience") following a real live incident that
 * day: tracked packages and their versions used to render as two separate
 * flat tables an operator had to visually cross-reference by package
 * name, a package with zero versions yet looked identical to "nothing is
 * being tracked at all," a failed build's actual error was a truncated
 * tooltip nobody would think to hover, and there was no way to tell from
 * this page whether internal/publish was even running. That rework
 * answered all three with one expandable table and an honest
 * pipeline-health indicator (still here) sourced from the SAME
 * fleetHealth.ts entities the Fleet page itself reads.
 *
 * SECOND rework, 2026-09-17, direct operator request following that
 * expandable-table design turning out not to be what was actually
 * wanted: "Tracked Packages" is now a genuinely flat table, one row per
 * (package, version, compilerVersion) — no more click-to-expand — with a
 * separate "Current status" (published/unpublished) column distinct from
 * "Compiler status" (the coarse queued/compiling/testing/published/error
 * bucket, COMPILER_STATUS_BUCKET below). Package-level admin (enable/
 * disable, PGO training, version tracking mode) moved to its own compact
 * "Packages" card above it, since it no longer has a natural home once
 * the versions table stopped nesting under a package row. "Run history"
 * (GET /v1/publish/runs, one row per raw NATS event — up to ~10 for a
 * single attempt) is REPLACED by "Current queue" — one row per pipeline
 * ATTEMPT, always visible (no more Show/Hide collapse), most-recently-
 * updated first.
 *
 * THIRD correction, same day, 2026-09-17: the first cut of "Current
 * queue" (GET /v1/publish/jobs, a new publish_jobs table) put one row per
 * real compile-job SUBMISSION within an attempt — i.e. up to 3 rows for
 * ONE pipeline run (package_test / full_project / addon), which read on
 * the page exactly like the ambiguity it was meant to fix: a "failed"
 * row next to a "succeeded" row from the very same attempt, with no way
 * to tell they were the same run. Direct operator correction: "Different
 * steps of one attempt should not be two separate rows. I wanted one row
 * per attempt with the status making it clear which step it was working
 * on at the time." publish_jobs and its endpoint are gone (migration
 * 0073); "Current queue" is now derived CLIENT-SIDE from the exact same
 * `packages[].versions[]` data "Tracked packages" already fetches (one
 * row per (package, version, compilerVersion), already the shape wanted)
 * — just re-sorted by `updated_at` descending.
 *
 * FOURTH rework, same day, 2026-09-17, direct operator request: "the
 * publish component does multiple builds and a whole workflow. Right now
 * this is very poorly visualized on the registry page. Make it much
 * better so I can see exactly what's in progress and what's done." A
 * single flat status label (even the granular one from the THIRD
 * correction above) still only says "compiling (full project)" — it
 * doesn't show that package-test already passed, or that addon/publish
 * are still ahead. "Current queue" now renders each attempt as a small
 * card with a 7-PILL STEPPER (computeStepper below — inspired by
 * internal/compiler/dashboard/frontend's LivePage.tsx phase-list stepper,
 * the "great work" this was modeled on) showing every real pipeline step
 * (package-test compile/run, full-project compile/run, addon
 * compile/verify, publish) as done/active/pending/failed/skipped at a
 * glance, backed by a NEW `failed_step` column (private/auth migration
 * 0074) that internal/publish now threads through from its own fail()
 * call sites instead of collapsing every failure to the single flat
 * value "failed" with no further detail.
 *
 * Data source: broker routes under `/v1/publish/*` and `/v1/fleet/*`,
 * proxied through this app's existing `/api/*` → broker `/v1/*` pattern
 * (../../worker/src/index.ts's ALLOWED_API_PREFIXES).
 */

/** Plan §4's state machine, verbatim — one pipeline attempt per
 *  (package, version, compilerVersion) triple moves through these in
 *  order, never skipping a step (goal 5: "avoid unnecessary compiles"). */
type PipelineStatus =
  | "pending"
  | "compiling_package_test"
  | "testing_package_test"
  | "compiling_full_project"
  | "testing_full_project"
  | "compiling_addon"
  | "verifying_addon"
  | "publishing"
  | "published"
  | "failed"
  // 2026-09-17, direct operator request: "I need a way to cancel it" —
  // a genuine operator-initiated stop, distinct from "failed" (an
  // unexpected error) so this reads as what actually happened instead
  // of an alarming-looking failure for something the operator asked for.
  | "cancelled"
  // 2026-10-06 (same-version, immutable publishing — internal/publish
  // types.ts): a verify-only re-check after a compiler release passed
  // (nothing uploaded); this build was rolled back to upstream; this build
  // was replaced by an operator rebuild.
  | "verified"
  | "withdrawn"
  | "superseded"
  // 2026-10-17: an operator "Re-run macOS benchmark" is measuring the
  // published build's macOS arm64 benchmark again (internal/publish
  // steps/macRebench.ts). Non-terminal: shown as the in-progress attempt
  // with Cancel; the build itself stays published.
  | "rebenching_macos";

/** Every state between "enqueued" and "done" — used to compute a
 *  package-level summary badge (below) and to give in-flight rows a
 *  visibly "alive" pulse rather than looking identical to a stalled one. */
const IN_PROGRESS_STATUSES = new Set<PipelineStatus>([
  "rebenching_macos",
  "compiling_package_test",
  "testing_package_test",
  "compiling_full_project",
  "testing_full_project",
  "compiling_addon",
  "verifying_addon",
  "publishing",
]);

/** Coarse "compiler status" bucket for the flattened versions table —
 *  direct operator request, 2026-09-17: "queued, compiling, testing,
 *  published, error" — 5 buckets, not §4's full 10-state machine. Folds
 *  every compiling_* state (plus "publishing", the npm-upload step) into
 *  "compiling" and every testing/verifying state into "testing". */
type CompilerStatusBucket =
  | "queued"
  | "compiling"
  | "testing"
  | "published"
  | "error"
  | "cancelled"
  | "verified"
  | "withdrawn"
  | "superseded"
  | "benchmarking";

const COMPILER_STATUS_BUCKET: Record<PipelineStatus, CompilerStatusBucket> = {
  rebenching_macos: "benchmarking",
  pending: "queued",
  compiling_package_test: "compiling",
  testing_package_test: "testing",
  compiling_full_project: "compiling",
  testing_full_project: "testing",
  compiling_addon: "compiling",
  verifying_addon: "testing",
  publishing: "compiling",
  published: "published",
  failed: "error",
  cancelled: "cancelled",
  verified: "verified",
  withdrawn: "withdrawn",
  superseded: "superseded",
};

const COMPILER_STATUS_VARIANT: Record<
  CompilerStatusBucket,
  "default" | "secondary" | "destructive" | "success"
> = {
  queued: "secondary",
  compiling: "default",
  testing: "default",
  published: "success",
  error: "destructive",
  cancelled: "secondary",
  verified: "success",
  withdrawn: "secondary",
  superseded: "secondary",
  benchmarking: "default",
};

/** Full §4 granular status, one label per state — used ONLY by "Current
 *  queue" (below), where the whole point (per the 2026-09-17 correction)
 *  is that a single row's status text alone must say which step of the
 *  attempt is currently running. "Tracked packages" keeps the coarser
 *  COMPILER_STATUS_BUCKET above — that table is scanned across many rows
 *  at once, where 5 buckets read faster than 10 granular states. */
const STATUS_LABEL: Record<PipelineStatus, string> = {
  pending: "pending",
  compiling_package_test: "compiling (package test)",
  testing_package_test: "testing (package test)",
  compiling_full_project: "compiling (full project)",
  testing_full_project: "testing (full project)",
  compiling_addon: "compiling (addon)",
  verifying_addon: "verifying (addon)",
  publishing: "publishing",
  published: "published",
  failed: "failed",
  cancelled: "cancelled",
  verified: "verified (re-check passed, not uploaded)",
  withdrawn: "withdrawn (this build is no longer pointed to)",
  // Legacy (2026-10-06 same-version scheme): a rebuild replaced its bytes.
  // Since the moving-pointer scheme older builds stay published/pinnable.
  superseded: "superseded by a rebuild",
  rebenching_macos: "re-running macOS benchmark",
};

const STATUS_VARIANT: Record<
  PipelineStatus,
  "default" | "secondary" | "destructive" | "success"
> = {
  pending: "secondary",
  compiling_package_test: "default",
  testing_package_test: "default",
  compiling_full_project: "default",
  testing_full_project: "default",
  compiling_addon: "default",
  verifying_addon: "default",
  publishing: "default",
  published: "success",
  failed: "destructive",
  // Muted, not alarming — the operator asked for this to stop.
  cancelled: "secondary",
  verified: "success",
  withdrawn: "secondary",
  superseded: "secondary",
  rebenching_macos: "default",
};

/** Mirrors the `versions` table sketch in §4 — one row per (package,
 *  version, compilerVersion) build attempt. `published_version` is the
 *  actual immutable npm version string once `status === "published"`
 *  (bare `8.17.0` for the first build, `8.17.0-pure.4.2.1` for a
 *  compiler-triggered rebuild — §6). `deprecated` is the mutable warning
 *  message currently set on that published version, if any (§7.6/Part B). */
interface PublishVersion {
  package_name: string;
  version: string;
  compiler_version: string | null;
  status: PipelineStatus;
  job_id_1: string | null;
  job_id_2: string | null;
  job_id_3: string | null;
  last_error: string | null;
  /** Which real pipeline step (PipelineStep below, or the "state_transition"
   *  catch-all for a generic top-level failure) was in flight when `status`
   *  became "failed" — private/auth migration 0074, 2026-09-17. Null
   *  whenever status isn't "failed", or on a row from before this column
   *  existed. Not a `PipelineStep` itself (broker stores it un-enforced,
   *  same discipline as `status`) — computeStepper below handles an
   *  unrecognized value the same as null. */
  failed_step: string | null;
  /** JSON EffectiveSettings the run resolved at its start (private/auth
   *  migration 0085; lib/publishToggles.ts parseRunSettings). Null/absent on
   *  a run from before the per-package toggles. */
  settings?: unknown;
  /** JSON TestsAfterPublish (private/auth migration 0093; lib/publishToggles.ts
   *  parseTestsAfterPublish): the package/full-project tests of a run with
   *  "Tests first" OFF, which run AFTER the publish. state "failed" = the
   *  build is published but those tests failed (flagged, not withdrawn).
   *  Null/absent: the run tested first. */
  tests_after_publish?: unknown;
  /** 'guided' | 'degraded' | null — set once step 3 (addon compile) runs
   *  its PGO two-job sequence for a package with pgo_train_enabled on;
   *  private/auth migration 0075_publish_versions_pgo_status.sql.
   *  'degraded' means the collection pass or local training run failed
   *  and step 3's own designed graceful-degrade path fired, producing an
   *  ordinary/untrained compile instead — NOT a pipeline failure, but
   *  previously invisible on this page (only ever a `log.warn` on the
   *  internal/publish host). Direct operator follow-up, 2026-09-21, after
   *  confirming a real collection-pass failure (job 4f679987) was exactly
   *  this correctly-designed behavior with no way to see it here. */
  pgo_status: string | null;
  /** private/auth migration 0076 (2026-10-02, operator: "make it so next
   *  time it happens the actual error is surfaced in the management ui"):
   *  why pgo_status is 'degraded' (internal/publish step 3's own reason —
   *  "collection-pass compile failed (job …): …" or "local training run
   *  failed: …" with the training run's stderr), the PGO collection-pass
   *  job id, and — when that job itself failed — the broker-resolved
   *  compile error from its build log (the compile API only ever hands
   *  internal/publish the error CODE). Null/absent on rows written by an
   *  older publisher or broker; the page then shows only the badge. */
  pgo_degraded_reason?: string | null;
  pgo_collect_job_id?: string | null;
  pgo_collect_error?: string | null;
  updated_at: string;
  published_version: string | null;
  deprecated: string | null;
  /** D1 migration 0079 row facts (see src/lib/versionState.ts): the version
   *  string the registry serves from this row's build (survives a failed
   *  re-run, cleared on withdraw), what the current/most recent attempt is
   *  for ('publish' | 'verify' | 'rebuild') and who requested it. Absent
   *  on rows last written by an older publisher. */
  published_as?: string | null;
  run_mode?: string | null;
  requested_by?: string | null;
  /** D1 migration 0080: JSON NativeTargetRecord[] of the row's build
   *  (src/lib/nativeTargets.ts) — per platform package, and the decoupled
   *  darwin-arm64 follow-up's state (pending / published / failed: reason). */
  native_targets?: string | null;
  /** D1 migration 0081 (a run's history entry carries the already-parsed
   *  array): every build of the attempt in order — compile jobs and the
   *  local training runs — with kind, label, seq, job id and status
   *  (src/lib/buildLabels.ts). Absent on rows from an older publisher. */
  compile_jobs?: string | unknown[] | null;
  /** D1 migration 0083 (a run's history entry carries the already-parsed
   *  array): the attempt's step checklist — every pipeline step and
   *  optimisation sub-step with state, duration, builds and reason
   *  (src/lib/stepChecklist.ts). Absent/null = "no step detail". */
  steps?: string | unknown[] | null;
  /** A request this page just sent, shown until the server's own row
   *  arrives (versionState.ts withLocalQueued) — never a real D1 row. */
  local_placeholder?: boolean;
  /** Only on historical attempts (parsed from a run_completed event's
   *  `diagnostics` — internal/publish types.ts PipelineDiagnostics). */
  diagnostics?: PipelineDiagnostics | null;
}

/** Mirrors internal/publish src/types.ts's PipelineDiagnostics — every
 *  field optional, parsed defensively (older events carry none). */
interface PipelineDiagnostics {
  pgoCollectJobId?: string;
  pgoDegradedReason?: string;
  addon?: {
    downloadedBytes?: number;
    tgzBytes?: number;
    unwrapped?: boolean;
    files?: string[];
    packageJson?: {
      name?: string;
      version?: string;
      main?: string;
      os?: string[];
      cpu?: string[];
      libc?: string[];
    };
    inspectError?: string;
    exports?: {
      type?: string;
      keyCount?: number;
      keys?: string[];
      loadError?: string;
    };
  };
}

/** Mirrors GET /v1/publish/runs's raw row shape (publish_runs — one row
 *  per COMPLETED attempt post the 2026-09-17 bloat fix). `detail_json` is
 *  the full run_completed event payload, JSON-encoded server-side —
 *  parsed client-side by parseRunToAttempt below, same "the broker stores
 *  it, the page interprets it" split the rest of this file already uses. */
interface PublishRunRow {
  id: number;
  package_name: string;
  version: string;
  compiler_version: string;
  event: string;
  detail_json: string;
  created_at: string;
}

/** Turns one publish_runs row into the SAME PublishVersion shape
 *  "Current queue" already renders, so AttemptCard/computeStepper need no
 *  special case for a historical row vs a live one. Returns null for a
 *  row this page can't make sense of (malformed detail_json, or missing
 *  the fields this parse actually needs) rather than throwing — one bad
 *  historical row must not blank the whole page. */
function parseRunToAttempt(row: PublishRunRow): PublishVersion | null {
  let detail: {
    status?: unknown;
    jobIds?: unknown;
    lastError?: unknown;
    failedStep?: unknown;
    pgoStatus?: unknown;
    pgoDegradedReason?: unknown;
    pgoCollectJobId?: unknown;
    pgoCollectError?: unknown;
    compileJobs?: unknown;
    steps?: unknown;
    timestamp?: unknown;
    diagnostics?: unknown;
    mode?: unknown;
    requestedBy?: unknown;
  };
  try {
    detail = JSON.parse(row.detail_json) as typeof detail;
  } catch {
    return null;
  }
  if (typeof detail.status !== "string" || typeof detail.timestamp !== "number")
    return null;
  const jobIds = Array.isArray(detail.jobIds) ? detail.jobIds : [];
  const diagnostics =
    detail.diagnostics && typeof detail.diagnostics === "object"
      ? (detail.diagnostics as PipelineDiagnostics)
      : null;
  const str = (x: unknown): string | null =>
    typeof x === "string" && x !== "" ? x : null;
  return {
    package_name: row.package_name,
    version: row.version,
    compiler_version: row.compiler_version,
    status: detail.status as PipelineStatus,
    job_id_1: (jobIds[0] as string | null | undefined) ?? null,
    job_id_2: (jobIds[1] as string | null | undefined) ?? null,
    job_id_3: (jobIds[2] as string | null | undefined) ?? null,
    last_error: typeof detail.lastError === "string" ? detail.lastError : null,
    failed_step:
      typeof detail.failedStep === "string" ? detail.failedStep : null,
    pgo_status: typeof detail.pgoStatus === "string" ? detail.pgoStatus : null,
    // Top-level fields from 2026-10-02 publishers; the `diagnostics` copy
    // is all an earlier publisher sent.
    pgo_degraded_reason:
      str(detail.pgoDegradedReason) ?? diagnostics?.pgoDegradedReason ?? null,
    pgo_collect_job_id:
      str(detail.pgoCollectJobId) ?? diagnostics?.pgoCollectJobId ?? null,
    pgo_collect_error: str(detail.pgoCollectError),
    compile_jobs: Array.isArray(detail.compileJobs)
      ? (detail.compileJobs as unknown[])
      : null,
    steps: Array.isArray(detail.steps) ? (detail.steps as unknown[]) : null,
    // Real epoch-ms from the original NATS event, not this row's own
    // created_at (D1 insert time — always slightly later, and less
    // accurate for "when did this attempt actually end"). An ISO string
    // WITH 'Z' passes fmtDate's normalization check unchanged.
    updated_at: new Date(detail.timestamp).toISOString(),
    published_version: null,
    deprecated: null,
    run_mode: str(detail.mode),
    requested_by: str(detail.requestedBy),
    diagnostics,
  };
}

/**
 * The pipeline's 7 REAL steps (§2/§4: compile → test → compile → test →
 * compile → verify → publish), in order — reuses internal/publish's own
 * RunKind values verbatim (its local audit log and, now, `failed_step`
 * above already speak this exact vocabulary) rather than inventing a
 * parallel one. Sign-off on these 7 names: 2026-09-17.
 */
type PipelineStep =
  | "compile_package_test"
  | "test_package_test"
  | "compile_full_project"
  | "test_full_project"
  | "compile_addon"
  | "verify_addon"
  | "publish";

const STEP_ORDER: PipelineStep[] = [
  "compile_package_test",
  "test_package_test",
  "compile_full_project",
  "test_full_project",
  "compile_addon",
  "verify_addon",
  "publish",
];

const STEP_LABEL: Record<PipelineStep, string> = {
  compile_package_test: "Package test · compile",
  test_package_test: "Package test · run",
  compile_full_project: "Full project · compile",
  test_full_project: "Full project · run",
  compile_addon: "Addon · compile",
  verify_addon: "Addon · verify",
  publish: "Publish",
};

// Index within STEP_ORDER "in flight" for each of §4's non-terminal
// statuses. "pending" and the two terminal statuses (published/failed)
// aren't here — computeStepper below handles those three as dedicated
// cases (published ⇒ every applicable step done; failed ⇒ pinned at
// failed_step; pending ⇒ nothing reached yet, index -1).
const IN_PROGRESS_STEP_INDEX: Partial<Record<PipelineStatus, number>> = {
  compiling_package_test: 0,
  testing_package_test: 1,
  compiling_full_project: 2,
  testing_full_project: 3,
  compiling_addon: 4,
  verifying_addon: 5,
  publishing: 6,
};

// The two full-project steps — the only ones that can legitimately be
// "skipped" (a package with no full-projects manifest entry never runs
// them at all — internal/publish/src/stateMachine.ts's hasFullProjectEntry
// branch skips straight from testing_package_test to compiling_addon).
const FULL_PROJECT_STEP_INDICES = new Set([2, 3]);

type StepState =
  "done" | "active" | "pending" | "failed" | "skipped" | "degraded";
interface StepperStep {
  key: PipelineStep | "benchmark";
  label: string;
  state: StepState;
  /** Extra hover text (the benchmark's progress / reason). */
  detail?: string;
}

/**
 * Derives a per-step render state for the 7-step pipeline straight from a
 * PublishVersion row — no extra API call, no guessing beyond what the row
 * already carries. See this file's header (FOURTH rework) for why this
 * exists.
 *
 * Full-project "skipped" detection: job_id_2 is ALWAYS set once step 2
 * genuinely runs (assigned before any success/failure is possible — see
 * stateMachine.ts's `if (step2.jobId) setStatus(...)`), so job_id_2 still
 * being null once the pipeline has moved on past where step 2 would have
 * run is a reliable "this package has no full-projects entry" signal, not
 * an in-progress or failed one.
 */
function computeStepper(v: PublishVersion): StepperStep[] {
  const fullProjectRan = v.job_id_2 != null;
  const build = (state: (i: number) => StepState): StepperStep[] =>
    STEP_ORDER.map((key, i) => ({
      key,
      label: STEP_LABEL[key],
      state: state(i),
    }));

  // Best-effort from job ids alone: nothing is pinned as THE step (that
  // would be a guess), just done-so-far vs never-reached. Shared by an
  // unrecognized/absent failed_step (the generic top-level-error
  // catch-all "state_transition", or a row from before that column
  // existed) AND "cancelled" (internal/publish's cancel() deliberately
  // sets no failed_step at all — it isn't a failure, so there's nothing
  // step-specific to blame, but "done so far" is still worth showing).
  const bestEffortFromJobIds = (i: number): StepState => {
    if (FULL_PROJECT_STEP_INDICES.has(i) && !fullProjectRan) return "skipped";
    if (i <= 1) return v.job_id_1 != null ? "done" : "pending";
    if (i <= 3) return v.job_id_2 != null ? "done" : "pending";
    if (i === 4) return v.job_id_3 != null ? "done" : "pending";
    return "pending"; // verify_addon/publish leave no job id of their own
  };

  if (
    v.status === "published" ||
    v.status === "rebenching_macos" ||
    v.status === "withdrawn" ||
    v.status === "superseded"
  ) {
    return build((i) =>
      FULL_PROJECT_STEP_INDICES.has(i) && !fullProjectRan ? "skipped" : "done",
    );
  }
  // Verify-only run: every check passed, the upload is skipped by design.
  if (v.status === "verified") {
    const publishIdx = STEP_ORDER.indexOf("publish");
    return build((i) =>
      i === publishIdx || (FULL_PROJECT_STEP_INDICES.has(i) && !fullProjectRan)
        ? "skipped"
        : "done",
    );
  }

  if (v.status === "cancelled") return build(bestEffortFromJobIds);

  if (v.status === "failed") {
    const failedIdx = v.failed_step
      ? STEP_ORDER.indexOf(v.failed_step as PipelineStep)
      : -1;
    if (failedIdx === -1) return build(bestEffortFromJobIds);
    return build((i) => {
      if (i === failedIdx) return "failed";
      if (FULL_PROJECT_STEP_INDICES.has(i) && !fullProjectRan) return "skipped";
      return i < failedIdx ? "done" : "pending";
    });
  }

  // "pending" (not started yet — curIdx -1, nothing reached) or a live
  // in-progress status.
  const curIdx = IN_PROGRESS_STEP_INDEX[v.status] ?? -1;
  return build((i) => {
    if (FULL_PROJECT_STEP_INDICES.has(i) && curIdx >= 4 && !fullProjectRan)
      return "skipped";
    if (i < curIdx) return "done";
    if (i === curIdx) return "active";
    return "pending";
  });
}

const STEP_STATE_STYLE: Record<StepState, string> = {
  done: "border-green-600/40 bg-green-600/10 text-green-700 dark:text-green-400",
  active: "border-primary bg-primary/10 text-primary",
  failed: "border-destructive bg-destructive/10 text-destructive",
  skipped: "border-border/50 text-muted-foreground/40 line-through",
  pending: "border-border text-muted-foreground/70",
  degraded:
    "border-yellow-500/60 bg-yellow-500/10 text-yellow-800 dark:text-yellow-300",
};

/** One pill in an attempt's stepper strip — see computeStepper above for
 *  how `step.state` is derived. */
function StepPill({ step }: { step: StepperStep }) {
  return (
    <span
      title={`${step.label} — ${step.state}${step.detail ? `\n${step.detail}` : ""}`}
      data-stage={step.key}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium whitespace-nowrap ${STEP_STATE_STYLE[step.state]}`}
    >
      {step.state === "active" && (
        <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse shrink-0" />
      )}
      {step.state === "done" && <span aria-hidden="true">✓</span>}
      {step.state === "failed" && <span aria-hidden="true">✕</span>}
      {step.state === "degraded" && <span aria-hidden="true">!</span>}
      {step.label}
    </span>
  );
}

/** Which upstream npm versions a tracked package follows (private/auth
 *  migration 0071_publish_version_tracking.sql; 2026-09-16, direct
 *  operator request: "package specific version selection and
 *  pattern-based tracking... default should be latest version only").
 *  "pattern" is a node-semver RANGE (e.g. "^4.0.0", "4.x"), not a glob
 *  and not an npm dist-tag name. */
type VersionTrackingMode = "latest" | "all" | "pattern";

const VERSION_MODE_LABEL: Record<VersionTrackingMode, string> = {
  latest: "latest only",
  all: "all versions",
  pattern: "pattern",
};

/** Mirrors the `packages` table (§4) — the operator-controlled
 *  allowlist. `versions` is every build attempt tracked for it so far. */
interface PublishPackage {
  name: string;
  enabled: boolean;
  /** Opt-in "custom PGO training" capability (private/auth migration
   *  0061_publish_pgo_train_flag.sql) — when set, internal/publish stages
   *  a training-workload script and compiles step 3 with `--train`
   *  (see internal/publish/src/steps/pgoTrainWorkload.ts). Default false. */
  pgo_train_enabled: boolean;
  /** The three optimisation toggles (type profile / LLVM PGO / text order):
   *  each one's override, global default and effective state, resolved by the
   *  broker (private/auth publishToggleModel.ts; lib/publishToggles.ts).
   *  Absent from an older broker. */
  settings?: SettingsView;
  version_mode: VersionTrackingMode;
  /** Only meaningful when version_mode === "pattern"; null otherwise. */
  version_pattern: string | null;
  added_at: string;
  added_by: string;
  versions: PublishVersion[];
}

/** One package's aggregate state, for the collapsed row's summary badge
 *  — computed client-side from its own `versions` array, no separate API.
 *  "none" (a tracked package with zero versions seen yet) is deliberately
 *  its OWN, calm, non-alarming state — the exact ambiguity a live
 *  incident this same day turned out to hinge on: "we're tracking
 *  lodash@latest and it hasn't published yet" reads completely
 *  differently from "nothing is happening," and before this rework the
 *  UI couldn't tell them apart at all. */
type PackageSummary =
  | { kind: "none" }
  | { kind: "failed"; count: number }
  | { kind: "in_progress"; count: number }
  | { kind: "cancelled"; count: number }
  | { kind: "pending"; count: number }
  | { kind: "published_pgo_degraded"; count: number }
  | { kind: "published_tests_failed"; count: number }
  | { kind: "published_tests_unfinished"; count: number }
  | { kind: "published" };

function summarizePackage(pkg: PublishPackage): PackageSummary {
  if (pkg.versions.length === 0) return { kind: "none" };
  const failed = pkg.versions.filter((v) => v.status === "failed").length;
  if (failed > 0) return { kind: "failed", count: failed };
  const inProgress = pkg.versions.filter((v) =>
    IN_PROGRESS_STATUSES.has(v.status),
  ).length;
  if (inProgress > 0) return { kind: "in_progress", count: inProgress };
  const cancelled = pkg.versions.filter((v) => v.status === "cancelled").length;
  if (cancelled > 0) return { kind: "cancelled", count: cancelled };
  const pending = pkg.versions.filter((v) => v.status === "pending").length;
  if (pending > 0) return { kind: "pending", count: pending };
  // "Tests first" OFF: published, but the package/full-project tests that ran
  // (or are still to run) AFTER the publish failed / have no verdict. A
  // failed verdict outranks every calm published state — the operator must
  // see it from the package list.
  const testsOf = (v: PublishVersion) =>
    v.status === "published"
      ? parseTestsAfterPublish(v.tests_after_publish)
      : null;
  const testsFailed = pkg.versions.filter(
    (v) => testsOf(v)?.state === "failed",
  ).length;
  if (testsFailed > 0)
    return { kind: "published_tests_failed", count: testsFailed };
  const testsUnfinished = pkg.versions.filter((v) => {
    const s = testsOf(v)?.state;
    return s === "pending" || s === "running" || s === "interrupted";
  }).length;
  if (testsUnfinished > 0)
    return { kind: "published_tests_unfinished", count: testsUnfinished };
  // Published, but the addon shipped without its training profile: a perf
  // regression the operator must see from the package list, not only inside
  // the version's attempt card (prod 2026-10-12: pretty-bytes 7.2.0 showed a
  // plain green "published" here while every PGO profile came out empty).
  const pgoDegraded = pkg.versions.filter(
    (v) => v.status === "published" && v.pgo_status === "degraded",
  ).length;
  if (pgoDegraded > 0)
    return { kind: "published_pgo_degraded", count: pgoDegraded };
  return { kind: "published" };
}

function PackageSummaryBadge({ summary }: { summary: PackageSummary }) {
  switch (summary.kind) {
    case "none":
      return (
        <Badge
          variant="secondary"
          title="Tracked, but npm hasn't been checked yet or no matching version exists"
        >
          tracking · no versions yet
        </Badge>
      );
    case "failed":
      return <Badge variant="destructive">{summary.count} failed</Badge>;
    case "in_progress":
      return (
        <Badge variant="default" className="animate-pulse">
          {summary.count} in progress
        </Badge>
      );
    case "cancelled":
      return <Badge variant="secondary">{summary.count} cancelled</Badge>;
    case "pending":
      return <Badge variant="secondary">{summary.count} queued</Badge>;
    case "published_pgo_degraded":
      return (
        <Badge
          variant="warning"
          title="Published, but the addon was built WITHOUT its PGO training profile (the training failed and the build degraded). Open the package: the attempt card shows the reason."
        >
          published · PGO degraded
          {summary.count > 1 ? ` (${summary.count})` : ""}
        </Badge>
      );
    case "published_tests_failed":
      return (
        <Badge
          variant="destructive"
          title="Published, but the package/full-project tests that run AFTER the publish (Tests first is off) FAILED. The build is not withdrawn. Open the package: the attempt shows which test and why."
        >
          published · tests failed after publish
          {summary.count > 1 ? ` (${summary.count})` : ""}
        </Badge>
      );
    case "published_tests_unfinished":
      return (
        <Badge
          variant="secondary"
          title="Published. The package/full-project tests run after the publish and the benchmark (Tests first is off) and have no verdict yet."
        >
          published · tests pending
          {summary.count > 1 ? ` (${summary.count})` : ""}
        </Badge>
      );
    case "published":
      return <Badge variant="success">published</Badge>;
  }
}

/** The deferred-tests flag of a published build (Tests first OFF): "tests
 *  failed after publish" (loud), or the calm pending / running / interrupted /
 *  skipped states. Nothing when the run tested first or the tests passed. */
function TestsAfterPublishBadge({
  v,
  className,
}: {
  v: PublishVersion;
  className?: string;
}) {
  const badge = testsAfterPublishBadge(
    parseTestsAfterPublish(v.tests_after_publish),
  );
  if (!badge) return null;
  return (
    <Badge
      variant={badge.variant}
      className={className}
      title={badge.title}
      data-testid="tests-after-publish-badge"
    >
      {badge.label}
    </Badge>
  );
}

/** Fleet-health entity shape (fleetHealth.ts's own FleetBox/FleetEntity,
 *  ../FleetPage.tsx mirrors this exactly) — only the fields this page
 *  actually reads. Pulled from the SAME /fleet/overview the Fleet page
 *  itself uses, filtered to `publish:<hostId>` entities, so "is the
 *  publish pipeline actually up" never has to be a separate trip to a
 *  different page to answer. */
type HealthState = "up" | "degraded" | "down" | "unknown";
interface FleetOverview {
  ok: true;
  boxes: Array<{
    hostname: string;
    entities: Array<{ entity: string; state: HealthState }>;
  }>;
}
interface PublishHostHealth {
  hostname: string;
  state: HealthState;
}

/** One "Current queue" row — a small card with the attempt's own header
 *  (package@version, compiler, overall status, updated time, and the
 *  relevant job id for pasting into the Jobs page) plus its 7-step
 *  stepper strip and, if failed, the full error inline. Replaces the old
 *  flat `<Table>` row (FOURTH rework, 2026-09-17) — a table row was too
 *  cramped for 7 pills alongside the existing columns.
 *
 * `historical` (2026-09-17, direct operator report: "Why can't I see
 * past ones that failed or got canceled?") marks a row sourced from
 * publish_runs rather than the live publish_versions snapshot — a PAST
 * attempt on this same identity that's since been superseded by a newer
 * one. Rendered slightly muted with a small badge so seeing two rows for
 * the same package@version reads as "history", not a bug; never gets a
 * Cancel button (nothing live to cancel).
 *
 * `onCancel`/`cancelling` (same date, "I need a way to cancel it"):
 * omitted entirely for a historical row or a terminal live one — only a
 * genuinely in-progress live attempt can be cancelled. */
function AttemptCard({
  v,
  historical = false,
  onCancel,
  cancelling = false,
  currentSettings,
}: {
  v: PublishVersion;
  historical?: boolean;
  onCancel?: () => void;
  cancelling?: boolean;
  /** The package's toggles as set NOW (to hint when a rebuild would differ). */
  currentSettings?: SettingsView;
}) {
  const runSettings = useMemo(() => parseRunSettings(v.settings), [v.settings]);
  const drift = useMemo(
    () => driftHint(settingsDrift(runSettings, currentSettings)),
    [runSettings, currentSettings],
  );
  const bench = useMemo(() => benchmarkStage(v.steps), [v.steps]);
  // The 13-step view replaces the thin stage strip + builds list whenever
  // the attempt has a step checklist; older attempts keep the strip.
  const hasSteps = useMemo(() => parseSteps(v.steps) !== null, [v.steps]);
  const steps = useMemo(() => computeStepper(v), [v]);
  const stepsInput = useMemo(
    () => ({
      status: v.status,
      steps: v.steps,
      nativeTargets: v.native_targets,
      settings: v.settings,
      packageName: v.package_name,
      publishedAs: v.published_as,
    }),
    [
      v.status,
      v.steps,
      v.native_targets,
      v.settings,
      v.package_name,
      v.published_as,
    ],
  );
  const benchBanner = benchmarkBanner(
    v.status === "published" && !historical ? bench : null,
    Date.now(),
    new Date(v.updated_at).getTime(),
  );
  const elapsed = useMemo(() => {
    const parsed = parseSteps(v.steps);
    const ms = parsed ? totalElapsedMs(parsed, Date.now()) : null;
    return ms === null ? null : fmtDurationMs(1, 1 + ms);
  }, [v.steps]);
  // Whichever of the (at most 3) real compile jobs this attempt has
  // reached is the relevant one to show — job_id_3 (addon) is only ever
  // set once the pipeline got that far, so preferring the LATEST-set id
  // always shows the job that was actually running (or last ran) for
  // this row's current status, including a mid-pipeline failure.
  const records = useMemo(
    () => parseCompileJobs(v.compile_jobs),
    [v.compile_jobs],
  );
  // The build the attempt is on (or ended on): "3/5 final (PGO)" — the
  // furthest compile job's id alone cannot say which of the run's builds
  // it is.
  const currentBuild =
    records.filter((r) => r.status === "running").at(-1) ?? records.at(-1);
  const jobId = currentBuild?.jobId ?? v.job_id_3 ?? v.job_id_2 ?? v.job_id_1;
  // A queued (`pending`) operator request is cancellable before it starts
  // (internal/publish force.ts) — except this page's own placeholder for a
  // request the pipeline hasn't acknowledged yet (seconds).
  const canCancel =
    !historical &&
    onCancel &&
    !v.local_placeholder &&
    (v.status === "pending" || IN_PROGRESS_STATUSES.has(v.status));
  const modeLabel = attemptModeLabel(v.run_mode);
  const attemptKey = historical
    ? `${v.package_name}@${v.version}~${v.updated_at}`
    : `${v.package_name}@${v.version}`;
  const liveAttempt =
    !historical &&
    (v.status === "pending" ||
      IN_PROGRESS_STATUSES.has(v.status) ||
      (v.status === "published" && bench?.state === "running"));
  // Compact card + detail drawer: the card shows the summary (progress,
  // current step, the four benchmark components); the full step list opens
  // in a sidebar (a full-screen sheet on phones). Both follow `v`, which the
  // page refreshes live, so the open drawer updates as steps change.
  // No clock here: a running step's elapsed time is an isolated <Elapsed>
  // inside the card, so a tick never re-renders the card or the drawer.
  const summary = useMemo(() => attemptSummary(stepsInput), [stepsInput]);
  const [detailOpen, setDetailOpen] = useState(false);
  const { step: deepLinkedStep, select: selectStep } =
    useStepSelection(attemptKey);
  // #run=<attempt>&step=<n> opens the detail (and the step panel in it).
  useEffect(() => {
    if (deepLinkedStep) setDetailOpen(true);
  }, [deepLinkedStep]);
  const closeDetail = () => {
    setDetailOpen(false);
    if (deepLinkedStep) selectStep(null);
  };
  const statusText =
    v.status === "pending" && v.run_mode === "rebuild"
      ? "queued (rebuild)"
      : v.status === "pending"
        ? "queued"
        : v.status === "published"
          ? publishedLabel(bench)
          : STATUS_LABEL[v.status];
  const onCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // A click on the card itself opens the detail; its own buttons / links
    // (copy, cancel, job link, the summary button) do their own thing.
    if (
      (e.target as HTMLElement).closest(
        "a,button,input,select,textarea,[role=button]",
      )
    )
      return;
    setDetailOpen(true);
  };
  return (
    <>
      <div
        onClick={onCardClick}
        className={`cursor-pointer rounded-lg border border-border p-3 space-y-2.5 ${historical ? "opacity-70" : ""}`}
        data-testid="attempt-card"
      >
        <div className="flex items-start gap-2">
          <div className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-x-4 gap-y-1">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 min-w-0">
              <span className="font-mono text-sm font-medium truncate">
                {v.package_name}@{v.version}
              </span>
              <CopyButton
                text={`${v.package_name}@${v.version}`}
                label="Copy package@version"
              />
              <span className="font-mono text-xs text-muted-foreground shrink-0">
                compiler {v.compiler_version ?? "—"}
              </span>
              {jobId && (
                <span
                  className="font-mono text-xs text-muted-foreground/70 shrink-0"
                  title="Job id of the furthest-progressed compile job for this attempt — see the Jobs page"
                >
                  job {jobId.slice(0, 8)}…
                </span>
              )}
              {currentBuild && (
                <Badge
                  variant="info"
                  className="font-normal"
                  title="The build of this publish run the attempt is on (or ended on) — all builds are listed below"
                >
                  {sequenceText(currentBuild.seq, records.length)}{" "}
                  {currentBuild.label}
                </Badge>
              )}
              {historical && (
                <Badge
                  variant="secondary"
                  className="font-normal"
                  title="A past attempt on this same package/version/compiler — superseded by a newer one below"
                >
                  past attempt
                </Badge>
              )}
              {modeLabel && (
                <span
                  className="text-xs text-muted-foreground shrink-0"
                  title="What this attempt is for, and who asked for it"
                >
                  {modeLabel}
                  {v.requested_by ? ` · requested by ${v.requested_by}` : ""}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge
                variant={STATUS_VARIANT[v.status]}
                className={
                  IN_PROGRESS_STATUSES.has(v.status)
                    ? "animate-pulse"
                    : undefined
                }
              >
                {statusText}
              </Badge>
              {v.deprecated && <Badge variant="warning">deprecated</Badge>}
              <PgoBadge v={v} />
              <TestsAfterPublishBadge v={v} />
              <span
                className="text-xs text-muted-foreground whitespace-nowrap"
                title={fmtDate(v.updated_at)}
              >
                <Ago at={parseDbTime(v.updated_at)} fmt={fmtAgo} /> ·{" "}
                {fmtDate(v.updated_at)}
                {elapsed && (
                  <span title="Total wall time of the run, benchmark included">
                    {" "}
                    · {elapsed}
                  </span>
                )}
              </span>
            </div>
          </div>
          {canCancel && (
            <CancelAttemptButton onCancel={onCancel!} cancelling={cancelling} />
          )}
        </div>

        {(v.status === "failed" || v.status === "cancelled") &&
          v.last_error && (
            <p
              className="line-clamp-2 break-words font-mono text-xs text-destructive"
              title="Open the details for the full error"
              data-testid="card-error"
            >
              {v.last_error}
            </p>
          )}

        {summary ? (
          <AttemptSummaryCard
            summary={summary}
            label={`${v.package_name}@${v.version}`}
            expanded={detailOpen}
            onOpen={() => setDetailOpen(true)}
          />
        ) : (
          // An attempt from before the step checklist: the old stage strip.
          <div className="flex flex-wrap items-center gap-1.5">
            {steps.map((s) => (
              <StepPill key={s.key} step={s} />
            ))}
            <Button
              variant="outline"
              size="sm"
              aria-haspopup="dialog"
              aria-expanded={detailOpen}
              onClick={() => setDetailOpen(true)}
            >
              Details
            </Button>
          </div>
        )}
      </div>

      <Drawer
        open={detailOpen}
        onClose={closeDetail}
        title={`${v.package_name}@${v.version}`}
        description={`compiler ${v.compiler_version ?? "—"}${modeLabel ? ` · ${modeLabel}` : ""}`}
        headerExtra={
          <>
            <Badge
              variant={STATUS_VARIANT[v.status]}
              className={
                IN_PROGRESS_STATUSES.has(v.status)
                  ? "mt-1 animate-pulse"
                  : "mt-1"
              }
            >
              {statusText}
            </Badge>
            {canCancel && (
              <CancelAttemptButton
                onCancel={onCancel!}
                cancelling={cancelling}
                className="mr-2"
              />
            )}
          </>
        }
        data-testid="attempt-detail"
      >
        {benchBanner && (
          <Alert
            variant="default"
            className="py-2"
            data-testid="benchmark-banner"
          >
            <AlertDescription className="text-xs">
              {benchBanner}
            </AlertDescription>
          </Alert>
        )}

        <StepProblemBanner
          steps={v.steps}
          nativeTargets={v.native_targets}
          status={v.status}
        />

        {(v.status === "failed" || v.status === "cancelled") &&
          v.last_error && (
            <Alert variant="destructive" className="py-2">
              <AlertDescription className="relative max-h-48 overflow-y-auto font-mono text-xs whitespace-pre-wrap break-words">
                <CopyButton
                  text={v.last_error}
                  label="Copy error"
                  className="absolute right-0 top-0"
                />
                {v.last_error}
              </AlertDescription>
            </Alert>
          )}

        {!hasSteps && (
          <AttemptBuilds
            compileJobs={v.compile_jobs}
            nativeTargets={v.native_targets}
            status={v.status}
            live={
              !historical &&
              (v.status === "pending" || IN_PROGRESS_STATUSES.has(v.status))
            }
          />
        )}

        {runSettings && (
          <div
            className="text-xs text-muted-foreground"
            data-testid="run-settings"
          >
            <span className="font-medium">Settings of this run:</span>{" "}
            {runSettingsLine(runSettings)}
            {drift && (
              <div className="text-amber-600 dark:text-amber-400">{drift}</div>
            )}
          </div>
        )}

        <PublishSteps
          attemptKey={attemptKey}
          input={stepsInput}
          live={liveAttempt}
        />

        <PgoDegradedPanel v={v} />

        <AttemptDiagnostics v={v} />

        <BenchDiagnostics
          packageName={v.package_name}
          version={v.version}
          steps={v.steps}
          attemptKey={attemptKey}
        />
      </Drawer>
    </>
  );
}

/** The PGO outcome of an attempt's addon compile — "PGO: guided" when the
 *  collection pass + local training run produced a profile the final
 *  compile used, "PGO: degraded" when either failed and step 3 fell back
 *  to an untrained compile. Nothing for a package with PGO training off.
 *  Shared by "Current queue" and the versions table. */
/** Whether THIS row's build is published on the registry ("published" —
 *  a pinnable X.Y.Z-pure.<cv> build; which one X.Y.Z points at is in the
 *  builds list below the version), was withdrawn, or neither
 *  ("unpublished"). A queued or failed re-run of a published build still
 *  reads "published": its build is still there (versionState.ts
 *  servesBuild). */
function BuildStatusBadge({ v }: { v: PublishVersion }) {
  if (servesBuild(v))
    return (
      <Badge
        variant="success"
        title={v.published_as ? `published as ${v.published_as}` : undefined}
      >
        {publishedLabel(benchmarkStage(v.steps))}
      </Badge>
    );
  if (v.status === "withdrawn")
    return (
      <Badge
        variant="secondary"
        title="Withdrawn: this build is no longer pointed to (it stays installable when pinned)"
      >
        withdrawn
      </Badge>
    );
  return <Badge variant="secondary">unpublished</Badge>;
}

/** The macOS (darwin-arm64) build of this row's version: it follows the
 *  published linux build (internal/publish steps/darwinFollowUp.ts) —
 *  "macOS: pending" while it waits for or builds on a runner, "macOS:
 *  published" once a build with it serves the version, "macOS: failed:
 *  <reason>" when it failed, timed out, was abandoned in the queue or was
 *  superseded (linux stays live either way). Nothing when darwin is off. */
function MacosBadge({
  v,
  className,
}: {
  v: PublishVersion;
  className?: string;
}) {
  const s = macosState(v.native_targets);
  if (!s) return null;
  if (s.kind === "pending")
    return (
      <Badge variant="info" className={className} title={s.detail}>
        {s.label}
      </Badge>
    );
  if (s.kind === "published")
    return (
      <Badge variant="success" className={className} title={s.detail}>
        {s.label}
      </Badge>
    );
  return (
    <Badge
      variant="warning"
      className={cn("max-w-md truncate", className)}
      title={`${s.reason}\n\nThe linux build stays live; Rebuild starts a new darwin-arm64 attempt.`}
    >
      {s.label}
    </Badge>
  );
}

/** Hardware verification of the darwin addon on the Mac (publish
 *  steps/macVerify.ts, macosVerifyState): verified / unverified / verify
 *  failed. Only shown once the darwin build exists, so "unverified" is never
 *  noise on a version that has no macOS build. */
function MacosVerifyBadge({
  v,
  className,
}: {
  v: PublishVersion;
  className?: string;
}) {
  const s = macosVerifyState(v.native_targets);
  if (!s) return null;
  const variant =
    s.kind === "verified"
      ? "success"
      : s.kind === "failed"
        ? "destructive"
        : "secondary";
  return (
    <Badge
      variant={variant}
      className={cn("max-w-md truncate", className)}
      title={s.detail}
    >
      {s.label}
    </Badge>
  );
}

/** The macOS benchmark outcome next to MacosBadge (publish bench/macos.ts):
 *  benchmarked / noisy / bench invalid / bench unavailable. */
function MacosBenchBadge({
  v,
  className,
}: {
  v: PublishVersion;
  className?: string;
}) {
  const s = macosBenchState(v.native_targets);
  if (!s) return null;
  const variant =
    s.kind === "benchmarked"
      ? "success"
      : s.kind === "unavailable"
        ? "secondary"
        : "warning";
  return (
    <Badge
      variant={variant}
      className={cn("max-w-md truncate", className)}
      title={s.detail}
    >
      {s.label}
    </Badge>
  );
}

function PgoBadge({ v, className }: { v: PublishVersion; className?: string }) {
  if (v.pgo_status === "guided")
    return (
      <Badge
        variant="secondary"
        className={className}
        title="This attempt's addon compile consumed a real, freshly-collected type profile: the compiler's build record confirmed it (same sha256, rows matched) after PGO training succeeded."
      >
        PGO: guided
      </Badge>
    );
  if (v.pgo_status === "degraded")
    return (
      <Badge
        variant="warning"
        className={className}
        title="A PGO layer (type profile, LLVM PGO or the package text order) failed, so this attempt's addon was built without it. Not a pipeline failure — the panel below the attempt names the layer and its cause."
      >
        PGO: degraded
      </Badge>
    );
  return null;
}

/** WHY PGO degraded, visible without opening anything: the first line of
 *  internal/publish's reason inline, the collection job (link to its build
 *  log), and expandable sections for the full reason and — when the
 *  collection compile itself failed — the real compile error the broker
 *  pulled from that job's build log. Renders nothing for a non-degraded
 *  attempt, or for a degraded one written by an older publisher that sent
 *  no reason (the badge alone then says what's known). Operator request,
 *  2026-10-02: "make it so next time it happens the actual error is
 *  surfaced in the management ui". */
function hasPgoDegradedDetail(v: PublishVersion): boolean {
  return (
    v.pgo_status === "degraded" &&
    !!(v.pgo_degraded_reason || v.pgo_collect_error || v.pgo_collect_job_id)
  );
}

function PgoDegradedPanel({ v }: { v: PublishVersion }) {
  if (!hasPgoDegradedDetail(v)) return null;
  const reason = v.pgo_degraded_reason ?? null;
  const collectError = v.pgo_collect_error ?? null;
  const collectJobId = v.pgo_collect_job_id ?? null;
  // The headline names the LAYER that failed (lib/pgoLayers.ts): a
  // succeeded type-profile collection must not be reported as the failure
  // when the LLVM instrumented compile was what died (prod 2026-10-14).
  const layers = pgoLayers({
    steps: v.steps,
    reason,
    collectJobId,
    status: v.pgo_status,
  });
  const typeProfileFailed = layers.some(
    (l) => l.name === "Type profile" && l.state === "failed",
  );
  const firstLine = reason?.split("\n", 1)[0] ?? null;
  const reasonIsLong = !!reason && reason !== firstLine;
  return (
    <div className="rounded-md border border-yellow-500/50 bg-yellow-500/10 px-3 py-2 text-xs space-y-1.5">
      <div className="font-semibold text-amber-700 dark:text-amber-300">
        {layers.length > 0 ? pgoHeadline(layers) : "PGO: degraded"}
      </div>
      {layers.length > 0 ? (
        <ul className="space-y-0.5" data-testid="pgo-layers">
          {layers.map((l) => (
            <li
              key={l.name}
              className={cn(
                "font-mono whitespace-pre-wrap break-words",
                l.state === "failed" && "text-foreground",
                l.state !== "failed" && "text-muted-foreground",
              )}
            >
              <span className="font-sans font-medium">{l.name}:</span>{" "}
              {l.state === "ok"
                ? "ok"
                : l.state === "failed"
                  ? `failed — ${l.text}`
                  : l.state === "disabled"
                    ? l.text
                    : `off — ${l.text}`}
              {l.jobId && (
                <>
                  {" "}
                  (<JobLink id={l.jobId} label="job" />)
                </>
              )}
            </li>
          ))}
        </ul>
      ) : (
        firstLine && (
          <div className="font-mono whitespace-pre-wrap break-words">
            {firstLine}
          </div>
        )
      )}
      {reasonIsLong && (
        <details>
          <summary className="cursor-pointer select-none text-muted-foreground">
            Full reason ({reason!.length.toLocaleString()} chars)
          </summary>
          <pre className="mt-1 max-h-96 overflow-auto whitespace-pre-wrap break-words font-mono">
            {reason}
          </pre>
        </details>
      )}
      {collectError && (typeProfileFailed || layers.length === 0) && (
        <details open={!reasonIsLong}>
          <summary className="cursor-pointer select-none text-muted-foreground">
            Collection compile error (from the job's build log)
          </summary>
          <pre className="mt-1 max-h-96 overflow-auto whitespace-pre-wrap break-words font-mono">
            {collectError}
          </pre>
        </details>
      )}
    </div>
  );
}

function fmtBytes(n: number | undefined): string {
  if (n === undefined) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  return `${(n / 1024 / 1024).toFixed(1)} MiB`;
}

function JobLink({
  id,
  label,
}: {
  id: string | null | undefined;
  label: string;
}) {
  if (!id) return null;
  return (
    <Link
      to={`/logs/${encodeURIComponent(id)}`}
      className="font-mono underline decoration-dotted hover:text-foreground"
      title={`Open job ${id} (build log, flags, artifact download)`}
    >
      {label ? `${label} ` : ""}
      {id.slice(0, 8)}
    </Link>
  );
}

/** Everything known about how this attempt got where it did: every compile
 *  job (including the PGO collection pass, which has no job_id slot of its
 *  own), why PGO degraded, and what the addon artifact actually contained.
 *  Added 2026-09-24 after lodash@4.18.1's "spawnSync npm ENOBUFS" took an
 *  R2 download + local repro to explain — the answer (gzip-wrapped .tgz,
 *  libc: ["musl"], zero exports) belongs on this page. */
function AttemptDiagnostics({ v }: { v: PublishVersion }) {
  const d = v.diagnostics;
  const addon = d?.addon;
  const pj = addon?.packageJson;
  const ex = addon?.exports;
  const pgoCollectJobId = v.pgo_collect_job_id ?? d?.pgoCollectJobId;
  const hasJobs = v.job_id_1 || v.job_id_2 || v.job_id_3 || pgoCollectJobId;
  if (!hasJobs && !d) return null;
  // An attempt that recorded its builds lists each one, named, in the
  // "Builds of this attempt" block above; the bare job slots (job_id_3 is
  // whichever addon build ran last) are only the fallback for an attempt
  // from a publisher that predates the list.
  const listed = parseCompileJobs(v.compile_jobs).length > 0;
  const warn = "text-amber-600 dark:text-amber-400";
  return (
    <details className="text-xs text-muted-foreground">
      <summary className="cursor-pointer select-none">Diagnostics</summary>
      <div className="mt-1.5 space-y-1 pl-3">
        {!listed && (
          <div className="flex flex-wrap gap-x-3 gap-y-0.5">
            <JobLink id={v.job_id_1} label="package test" />
            <JobLink id={v.job_id_2} label="full project" />
            <JobLink id={pgoCollectJobId} label="pgo collect" />
            <JobLink id={v.job_id_3} label="addon (last build)" />
          </div>
        )}
        {addon && (
          <div className="font-mono space-y-0.5">
            <div>
              artifact: downloaded {fmtBytes(addon.downloadedBytes)} → .tgz{" "}
              {fmtBytes(addon.tgzBytes)}
              {addon.unwrapped !== undefined &&
                ` (runner gzip wrapper ${addon.unwrapped ? "stripped" : "absent"})`}
            </div>
            {addon.inspectError && (
              <div className={warn}>
                tarball unreadable: {addon.inspectError}
              </div>
            )}
            {pj && (
              <div>
                package.json: {pj.name}@{pj.version} main={pj.main ?? "—"} os=
                {JSON.stringify(pj.os ?? null)} cpu=
                {JSON.stringify(pj.cpu ?? null)}{" "}
                <span className={pj.libc ? warn : undefined}>
                  libc={JSON.stringify(pj.libc ?? null)}
                </span>
              </div>
            )}
            {addon.files && addon.files.length > 0 && (
              <div className="break-words">files: {addon.files.join(", ")}</div>
            )}
            {ex && (
              <div
                className={ex.loadError || ex.keyCount === 0 ? warn : undefined}
              >
                require():{" "}
                {ex.loadError
                  ? `threw — ${ex.loadError}`
                  : `${ex.type}, ${ex.keyCount} export(s)${ex.keys?.length ? `: ${ex.keys.join(", ")}` : ""}`}
              </div>
            )}
          </div>
        )}
      </div>
    </details>
  );
}

function errMsg(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

/** 2026-09-17, direct operator report: "Please display the time in my
 *  local time not UTC." Root cause: every timestamp column this page
 *  reads (publish_versions.updated_at, publish_packages.added_at, ...)
 *  is written by private/auth's D1 schema as SQLite's `datetime('now')`
 *  — real UTC wall-clock time, but formatted as "YYYY-MM-DD HH:MM:SS"
 *  with no 'T', no 'Z', no offset at all. Most JS engines parse a string
 *  shaped like that as LOCAL time (no explicit zone marker ⇒ assume
 *  local) — for a value that's actually UTC, `new Date(...)` silently
 *  gets the wrong instant, and `.toLocaleString()` below just echoes the
 *  same raw UTC clock digits back instead of performing a real
 *  UTC→local conversion. Normalizing to an explicit UTC ISO string
 *  first (space → 'T', append 'Z') makes the parse unambiguous; an
 *  already-well-formed ISO string (has a 'T') passes through unchanged. */
function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(iso)
    ? `${iso.replace(" ", "T")}Z`
    : iso;
  const t = new Date(normalized);
  return Number.isNaN(t.getTime()) ? iso : t.toLocaleString();
}

/** "3m ago" / "just now" — short, glanceable, for the header's own
 *  last-refreshed indicator (every table row already has its own
 *  absolute timestamp via fmtDate; this one is deliberately relative). */
function fmtAgo(ms: number | null): string {
  if (ms === null) return "";
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

/** Epoch ms of a D1 timestamp (UTC, with or without the 'T'/'Z'), or null. */
function parseDbTime(iso: string | null): number | null {
  if (!iso) return null;
  const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(iso)
    ? `${iso.replace(" ", "T")}Z`
    : iso;
  const t = new Date(normalized).getTime();
  return Number.isNaN(t) ? null : t;
}

/** Small icon button that copies `text` (long ids / errors are truncated on
 *  screen, the full value is one click away). */
function CopyButton({
  text,
  label,
  className,
}: {
  text: string;
  label: string;
  className?: string;
}) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      aria-label={label}
      title={done ? "Copied" : label}
      className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${className ?? ""}`}
      onClick={() => {
        void navigator.clipboard
          .writeText(text)
          .then(() => {
            setDone(true);
            setTimeout(() => setDone(false), 1500);
          })
          .catch(() => undefined);
      }}
    >
      {done ? (
        <Check className="h-3 w-3" aria-hidden />
      ) : (
        <Copy className="h-3 w-3" aria-hidden />
      )}
    </button>
  );
}

export function RegistryPage() {
  const token = useAuthToken();
  const call = useCallback(
    <T,>(path: string, init?: RequestInit) => api<T>(token, path, init),
    [token],
  );

  const [packages, setPackages] = useState<PublishPackage[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  // HARD delete of builds (DeleteBuildsDialog): the builds ticked per
  // `<package>@<X.Y.Z>`, and the package + builds the open dialog is for.
  const [deleteSelection, setDeleteSelection] = useState<
    Record<string, string[]>
  >({});
  const [deleteTarget, setDeleteTarget] = useState<{
    pkg: string;
    versions: string[];
  } | null>(null);
  // The "updated 3s ago" stamp lives in its own cell: a refresh re-renders
  // only that label, never the page (lib/quietRefresh.ts).
  const lastLoaded = useMemo(() => createCell<number | null>(null), []);
  // Bumped once when the oldest local "queued" placeholder expires
  // (packagesView below); no periodic re-render of the page.
  const [expiryTick, setExpiryTick] = useState(0);
  // Stable row order while updates stream in (recentAttempts below).
  const attemptRanks = useRef(new Map<string, number>());
  const loadedOnce = useRef(false);

  const [packageFilter, setPackageFilter] = useState("");
  // Manual "act on a specific version" fields — see requestAction()'s own
  // comment for why this exists: a version can have failed on the box's
  // own local state with nothing ever relayed to this page (nothing to
  // put a per-row Republish button on). Now needs its OWN package select
  // too (2026-09-17 rework — no more per-package expand/collapse to nest
  // this inside, so it's no longer implicit which package it targets).
  const [manualPackage, setManualPackage] = useState("");
  const [manualVersion, setManualVersion] = useState("");

  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addVersionMode, setAddVersionMode] =
    useState<VersionTrackingMode>("latest");
  const [addVersionPattern, setAddVersionPattern] = useState("");
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  // Editing an already-tracked package's version-tracking mode — separate
  // dialog/state from Add above (Add is add-only; PATCH is edit-only, see
  // handleUpdatePublishPackage's own split on the broker side).
  const [trackingTarget, setTrackingTarget] = useState<PublishPackage | null>(
    null,
  );
  const [trackingMode, setTrackingMode] =
    useState<VersionTrackingMode>("latest");
  const [trackingPattern, setTrackingPattern] = useState("");
  const [trackingError, setTrackingError] = useState<string | null>(null);
  const [trackingSaving, setTrackingSaving] = useState(false);

  // The ONE pipeline action per version (src/lib/versionState.ts): the
  // version it targets, the state it was offered for, and the optional
  // reason a rebuild carries into the audit trail.
  const [actionTarget, setActionTarget] = useState<{
    pkg: string;
    version: string;
    state: VersionState<PublishVersion>;
    /** The action this dialog confirms: the version's primary action, or
     *  "Re-run macOS benchmark". */
    action: PrimaryAction;
  } | null>(null);
  const [actionReason, setActionReason] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSending, setActionSending] = useState(false);
  // Requests the broker accepted, shown as queued attempts until the
  // publish service's own `pending` row reaches D1 (withLocalQueued).
  const [localQueued, setLocalQueued] = useState<
    Map<string, LocalQueuedRequest>
  >(() => new Map());

  const [deprecateTarget, setDeprecateTarget] = useState<PublishVersion | null>(
    null,
  );
  const [deprecateMessage, setDeprecateMessage] = useState("");
  const [deprecateError, setDeprecateError] = useState<string | null>(null);
  const [deprecating, setDeprecating] = useState(false);

  // "Current queue" (formerly "Run history") — one row per pipeline
  // ATTEMPT (package, version, compilerVersion), most-recently-updated
  // first. Always visible now (no collapse). Built from TWO sources
  // merged in recentAttempts below: `packages` (live/current state,
  // "Tracked packages" already fetches this) for what's happening NOW,
  // plus `runsHistory` (GET /v1/publish/runs) for attempts that have
  // since been SUPERSEDED by a newer one on the same (package, version,
  // compilerVersion) — publish_versions only ever remembers the latest
  // attempt per identity, so a prior failed/cancelled run is invisible
  // there the moment a retry starts. Direct operator report, 2026-09-17:
  // "Why can't I see past ones that failed or got canceled? It seems to
  // only show what's actively in progress."
  const [queuePackageFilter, setQueuePackageFilter] = useState("");
  // The queue renders 10 attempts, "Show more" adds 10 (each attempt is tall).
  const [queueShown, setQueueShown] = useState(10);
  const [runsHistory, setRunsHistory] = useState<PublishRunRow[] | null>(null);
  const [cancellingKey, setCancellingKey] = useState<string | null>(null);

  const [publishHealth, setPublishHealth] = useState<
    PublishHostHealth[] | null
  >(null);

  // Every refresh is SILENT: the previous data stays on screen while it
  // revalidates, an unchanged response keeps the same object identities (so
  // nothing re-renders), a changed one replaces only what changed, a
  // transient failure never blanks the page, and pushes from the dashboard
  // socket are coalesced and held back while the tab is hidden or the user
  // is selecting text, typing or scrolling (lib/quietRefresh.ts).
  const load = useCallback(() => {
    call<{ ok: true; packages: PublishPackage[] }>("/publish/packages")
      .then((r) => {
        setPackages((prev) => shareStructure(prev, r.packages));
        setError(null);
        loadedOnce.current = true;
        lastLoaded.set(Date.now());
      })
      .catch((err) => {
        // A failed background refresh keeps the data already shown (the
        // "updated … ago" stamp shows it is stale); only a failed FIRST load
        // is an error state.
        if (!loadedOnce.current)
          setError(errMsg(err, "failed to load tracked packages"));
      });
  }, [call, lastLoaded]);

  useEffect(load, [load]);
  useEffect(() => {
    const quiet = quietly(load);
    const off = onDashboardChanged(token, quiet);
    return () => {
      off();
      quiet.cancel();
    };
  }, [token, load]);

  // Builds per version on registry.pure.dev (moving-pointer scheme,
  // src/lib/registryBuilds.ts): the pinnable X.Y.Z-pure.<cv> builds and
  // which one X.Y.Z points at. Public read-only registry route, refreshed
  // whenever the package list reloads. Quiet on failure (the panel says
  // the list is unavailable).
  const [registryBuilds, setRegistryBuilds] = useState<
    Map<string, RegistryBuildsView | { error: string }>
  >(() => new Map());
  const packageNames = useMemo(
    () =>
      (packages ?? [])
        .map((p) => p.name)
        .sort()
        .join("\n"),
    [packages],
  );
  // What the registry's build list depends on: which versions are published
  // as which build. Refetch when THAT changes, not on every package reload.
  const publishFingerprint = useMemo(
    () =>
      (packages ?? [])
        .flatMap((p) =>
          p.versions.map(
            (v) =>
              `${p.name}@${v.version}:${v.status}:${v.published_as ?? ""}:${v.deprecated ?? ""}`,
          ),
        )
        .sort()
        .join("\n"),
    [packages],
  );
  useEffect(() => {
    if (!packageNames) return;
    let cancelled = false;
    // VITE_PURE_REGISTRY_URL: a local dev run against a mock/dev registry.
    const registry =
      (import.meta.env.VITE_PURE_REGISTRY_URL as string | undefined) ??
      registryPublicUrl(window.location.hostname);
    void Promise.all(
      packageNames.split("\n").map(async (name) => {
        try {
          return [name, await fetchRegistryBuilds(registry, name)] as const;
        } catch (err) {
          return [name, { error: errMsg(err, "unreachable") }] as const;
        }
      }),
    ).then((entries) => {
      if (!cancelled)
        setRegistryBuilds((prev) =>
          shareMap(
            prev,
            new Map<string, RegistryBuildsView | { error: string }>(entries),
          ),
        );
    });
    return () => {
      cancelled = true;
    };
  }, [packageNames, publishFingerprint]);
  // Download counts per build + a daily series per package (registry
  // backend/downloads.ts, via the broker). Advisory: a failure only hides the
  // numbers (the builds table still renders).
  const [downloads, setDownloads] = useState<Map<string, DownloadsState>>(
    () => new Map(),
  );
  useEffect(() => {
    if (!packageNames) return;
    let cancelled = false;
    void Promise.all(
      packageNames.split("\n").map(async (name) => {
        try {
          const report = await call<DownloadsReport & { ok: boolean }>(
            `/publish/downloads?package=${encodeURIComponent(name)}&days=60`,
          );
          return [name, { ok: true, report }] as const;
        } catch (err) {
          return [
            name,
            { ok: false, error: errMsg(err, "unavailable") },
          ] as const;
        }
      }),
    ).then((entries) => {
      if (!cancelled)
        setDownloads((prev) =>
          shareMap(prev, new Map<string, DownloadsState>(entries as never)),
        );
    });
    return () => {
      cancelled = true;
    };
  }, [packageNames, publishFingerprint, call]);

  // Quiet by design: a failure here just means the health pill/banner
  // stays hidden rather than blocking the rest of the page — packages
  // and versions are the primary content, pipeline health is context.
  const loadPublishHealth = useCallback(() => {
    call<FleetOverview>("/fleet/overview")
      .then((r) => {
        const hosts: PublishHostHealth[] = [];
        for (const box of r.boxes) {
          for (const e of box.entities) {
            if (e.entity.startsWith("publish:"))
              hosts.push({ hostname: box.hostname, state: e.state });
          }
        }
        setPublishHealth((prev) => shareStructure(prev, hosts));
      })
      .catch(() => {
        /* keep the last known health: a blip must not flash the pill away */
      });
  }, [call]);
  useEffect(loadPublishHealth, [loadPublishHealth]);
  useEffect(() => {
    const quiet = quietly(loadPublishHealth);
    const off = onDashboardChanged(token, quiet);
    return () => {
      off();
      quiet.cancel();
    };
  }, [token, loadPublishHealth]);

  // Quiet by design, same reasoning as loadPublishHealth above — history
  // is a "nice to have alongside" for Current queue, not primary content;
  // a failure here just means older/superseded attempts stay hidden
  // rather than blocking the live rows "Tracked packages" already shows.
  const loadRunsHistory = useCallback(() => {
    call<{ ok: true; runs: PublishRunRow[] }>("/publish/runs?limit=100")
      .then((r) => setRunsHistory((prev) => shareStructure(prev, r.runs)))
      .catch(() => {
        /* keep the history already shown: a blip must not drop the rows */
      });
  }, [call]);
  useEffect(loadRunsHistory, [loadRunsHistory]);
  useEffect(() => {
    const quiet = quietly(loadRunsHistory);
    const off = onDashboardChanged(token, quiet);
    return () => {
      off();
      quiet.cancel();
    };
  }, [token, loadRunsHistory]);

  // `packages` plus a placeholder `pending` row for every request this page
  // just sent that the server doesn't show yet — so a click is visible on
  // the version row and in "Current queue" at once (versionState.ts
  // withLocalQueued). Everything below reads this, not `packages`.
  const packagesView = useMemo(() => {
    if (!packages) return null;
    const now = Date.now();
    return packages.map((pkg) => {
      const groups = groupByVersion(pkg.versions);
      let versions = pkg.versions;
      for (const q of localQueued.values()) {
        if (q.pkg !== pkg.name) continue;
        const rows = groups.get(versionKey(q.pkg, q.version)) ?? [];
        const shown = withLocalQueued(rows, q, now, (lq) => ({
          package_name: lq.pkg,
          version: lq.version,
          compiler_version: null,
          status: "pending" as const,
          job_id_1: null,
          job_id_2: null,
          job_id_3: null,
          last_error: null,
          failed_step: null,
          pgo_status: null,
          updated_at: new Date(lq.at).toISOString(),
          published_version: null,
          deprecated: null,
          published_as: null,
          run_mode: lq.kind === "rebuild" ? "rebuild" : "publish",
          requested_by: lq.requestedBy,
          local_placeholder: true,
        }));
        if (shown.length !== rows.length) versions = [shown[0]!, ...versions];
      }
      return versions === pkg.versions ? pkg : { ...pkg, versions };
    });
    // `expiryTick`: re-evaluate placeholder expiry (LOCAL_QUEUED_TTL_MS).
  }, [packages, localQueued, expiryTick]);
  // One timeout for the oldest placeholder instead of a periodic re-render.
  useEffect(() => {
    if (localQueued.size === 0) return;
    const oldest = Math.min(...[...localQueued.values()].map((q) => q.at));
    const t = setTimeout(
      () => setExpiryTick((n) => n + 1),
      Math.max(0, oldest + LOCAL_QUEUED_TTL_MS - Date.now()) + 100,
    );
    return () => clearTimeout(t);
  }, [localQueued, expiryTick]);

  // One derived state per (package, version) — drives the one action a
  // version offers (src/lib/versionState.ts).
  const versionStates = useMemo(() => {
    const out = new Map<string, VersionState<PublishVersion>>();
    for (const pkg of packagesView ?? [])
      for (const [key, rows] of groupByVersion(pkg.versions))
        out.set(key, deriveVersionState(rows));
    return out;
  }, [packagesView]);
  const stateOf = (pkg: string, version: string) =>
    versionStates.get(versionKey(pkg, version)) ??
    deriveVersionState<PublishVersion>([]);

  const filteredPackages = useMemo(() => {
    const q = packageFilter.trim().toLowerCase();
    if (!q) return packagesView ?? [];
    return (packagesView ?? []).filter((p) => p.name.toLowerCase().includes(q));
  }, [packagesView, packageFilter]);

  // Flattened (package, version, compilerVersion) rows — direct operator
  // request, 2026-09-17: "should only show one row per package-version-
  // compilerversion combination... if we track 20 versions then 20 rows
  // and if we support two compiler versions then 40 rows." Replaces the
  // old package-row-that-expands-to-show-its-versions structure with one
  // flat table, still respecting the same package-name filter above.
  const flattenedVersions = useMemo(
    () =>
      filteredPackages.flatMap((pkg) => pkg.versions.map((v) => ({ pkg, v }))),
    [filteredPackages],
  );

  // "Current queue" — every tracked version PLUS genuinely-superseded past
  // attempts on the same identity, most-recently-updated first, capped at
  // 50. Deliberately built from ALL `packages` (not `filteredPackages`/
  // `flattenedVersions` above, which respect the separate "Tracked
  // packages" name filter) and given its own `queuePackageFilter`, since
  // an operator watching the queue is usually NOT also filtering the
  // packages table by the same name at the same time. One row per
  // (package, version, compilerVersion) ATTEMPT — see this file's header
  // comment for why that's the whole point of the 2026-09-17 correction,
  // not a per-job-kind breakdown.
  //
  // History merge (2026-09-17, direct operator report: "Why can't I see
  // past ones that failed or got canceled?"): `packages[].versions[]`
  // only ever carries the LATEST attempt per (package, version,
  // compilerVersion) — a prior attempt on the exact same identity is
  // invisible there the moment a newer one starts. runsHistory
  // (publish_runs, one row per COMPLETED attempt) still has it. For each
  // (package, version, compilerVersion) key: if a live row exists, its
  // current state was produced by the single NEWEST run under that key
  // (exclude that one — it's already represented live); every OLDER run
  // under the same key is genuine, now-superseded history. If no live row
  // exists at all for a key (fully untracked/pruned since), every run
  // under it is history.
  const recentAttempts = useMemo(() => {
    const live = (packagesView ?? []).flatMap((pkg) => pkg.versions);
    const keyOf = (v: {
      package_name: string;
      version: string;
      compiler_version: string | null;
    }) => `${v.package_name}:${v.version}:${v.compiler_version ?? ""}`;

    const liveKeys = new Set(live.map(keyOf));
    const runsByKey = new Map<string, PublishVersion[]>();
    for (const row of runsHistory ?? []) {
      const attempt = parseRunToAttempt(row);
      if (!attempt) continue;
      const key = keyOf(attempt);
      const bucket = runsByKey.get(key);
      if (bucket) bucket.push(attempt);
      else runsByKey.set(key, [attempt]);
    }
    const historical: PublishVersion[] = [];
    for (const [key, runs] of runsByKey) {
      runs.sort(
        (a, b) =>
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      );
      historical.push(...(liveKeys.has(key) ? runs.slice(1) : runs));
    }

    const q = queuePackageFilter.trim().toLowerCase();
    const merged: Array<{ v: PublishVersion; historical: boolean }> = [
      ...live.map((v) => ({ v, historical: false })),
      ...historical.map((v) => ({ v, historical: true })),
    ];
    const matching = q
      ? merged.filter((r) => r.v.package_name.toLowerCase().includes(q))
      : merged;
    // Newest first when a row first appears, then it keeps its place while
    // it updates (a live attempt must not jump under the pointer).
    return stableOrder(
      matching,
      (r) =>
        r.historical ? `h:${keyOf(r.v)}:${r.v.updated_at}` : `c:${keyOf(r.v)}`,
      (r) => new Date(r.v.updated_at).getTime(),
      attemptRanks.current,
    ).slice(0, 50);
  }, [packagesView, runsHistory, queuePackageFilter]);

  // Page-top banner: current (non-historical) attempts of the last 48 h
  // whose step checklist has a degraded or failed row (lib/stepChecklist.ts).
  const problemAttempts = useMemo(
    () =>
      attemptsWithProblems(
        recentAttempts
          .filter(
            ({ v, historical }) =>
              !historical &&
              Date.now() - new Date(v.updated_at).getTime() < 48 * 3_600_000,
          )
          .map(({ v }) => v),
      ),
    [recentAttempts],
  );

  async function submitAdd() {
    const name = addName.trim();
    if (!name) {
      setAddError("package name is required");
      return;
    }
    if (addVersionMode === "pattern" && !addVersionPattern.trim()) {
      setAddError(
        'a version pattern is required when tracking mode is "pattern"',
      );
      return;
    }
    setAdding(true);
    setAddError(null);
    try {
      await call("/publish/packages", {
        method: "POST",
        body: JSON.stringify({
          name,
          version_mode: addVersionMode,
          version_pattern:
            addVersionMode === "pattern" ? addVersionPattern.trim() : undefined,
        }),
      });
      setAddOpen(false);
      setAddName("");
      setAddVersionMode("latest");
      setAddVersionPattern("");
      toast(`${name} added to the tracked allowlist`);
      load();
    } catch (err) {
      setAddError(errMsg(err, "failed to add package"));
    } finally {
      setAdding(false);
    }
  }

  async function toggleEnabled(pkg: PublishPackage) {
    const key = `toggle:${pkg.name}`;
    setBusyKey(key);
    try {
      await call(`/publish/packages/${encodeURIComponent(pkg.name)}`, {
        method: "PATCH",
        body: JSON.stringify({ enabled: !pkg.enabled }),
      });
      toast(`${pkg.name} ${pkg.enabled ? "disabled" : "enabled"}`);
      load();
    } catch (err) {
      setError(errMsg(err, "failed to update package"));
    } finally {
      setBusyKey(null);
    }
  }

  async function deletePackage(pkg: PublishPackage) {
    if (
      !window.confirm(
        `Delete "${pkg.name}" from tracking? It has no versions, so nothing is lost.`,
      )
    )
      return;
    const key = `delete:${pkg.name}`;
    setBusyKey(key);
    try {
      await call(`/publish/packages/${encodeURIComponent(pkg.name)}`, {
        method: "DELETE",
      });
      toast(`${pkg.name} deleted`);
      load();
    } catch (err) {
      setError(errMsg(err, "failed to delete package"));
    } finally {
      setBusyKey(null);
    }
  }

  // One of the three optimisation switches (type profile / LLVM PGO / text
  // order): operator-only PUT on the broker, which stores it, audits it and
  // relays it to internal/publish. Applies to the next publish/rebuild.
  async function setPackageSetting(
    pkg: PublishPackage,
    key: SettingKey,
    to: SettingOverride,
  ) {
    setBusyKey(`${pkg.name}:${key}`);
    try {
      await call(`/publish/packages/${encodeURIComponent(pkg.name)}/settings`, {
        method: "PUT",
        body: JSON.stringify(settingChange(key, to)),
      });
      toast(
        `${TOGGLE_TITLE[key]} for ${pkg.name}: ${to === "inherit" ? "default" : to}. Applies to the next publish or rebuild.`,
      );
      load();
    } catch (err) {
      setError(errMsg(err, `failed to update ${TOGGLE_TITLE[key]} setting`));
    } finally {
      setBusyKey(null);
    }
  }

  function openTracking(pkg: PublishPackage) {
    setTrackingTarget(pkg);
    setTrackingMode(pkg.version_mode);
    setTrackingPattern(pkg.version_pattern ?? "");
    setTrackingError(null);
  }

  async function submitTracking() {
    if (!trackingTarget) return;
    if (trackingMode === "pattern" && !trackingPattern.trim()) {
      setTrackingError(
        'a version pattern is required when tracking mode is "pattern"',
      );
      return;
    }
    setTrackingSaving(true);
    setTrackingError(null);
    try {
      await call(
        `/publish/packages/${encodeURIComponent(trackingTarget.name)}`,
        {
          method: "PATCH",
          body: JSON.stringify({
            version_mode: trackingMode,
            version_pattern:
              trackingMode === "pattern" ? trackingPattern.trim() : undefined,
          }),
        },
      );
      toast(
        `${trackingTarget.name} now tracks ${VERSION_MODE_LABEL[trackingMode]}${
          trackingMode === "pattern" ? ` "${trackingPattern.trim()}"` : ""
        }`,
      );
      setTrackingTarget(null);
      load();
    } catch (err) {
      setTrackingError(errMsg(err, "failed to update version tracking"));
    } finally {
      setTrackingSaving(false);
    }
  }

  // The ONE pipeline action a version offers (src/lib/versionState.ts):
  // "Rebuild with current compiler" for a version the registry serves (or
  // withdrew), "Publish"/"Retry publish" for one that failed, was cancelled
  // or never published, nothing while an attempt is queued or running.
  // Takes a plain (packageName, version) pair so the manual entry field
  // (a version this page may have no row for — e.g. one that failed on the
  // box before the event relay existed, 2026-09-17) shares the same logic.
  // Operator report 2026-10-12: a per-row "Republish" next to "Rebuild"
  // was a silent no-op for a published version.
  function requestAction(pkg: string, version: string, kind?: "rebench-macos") {
    const state = stateOf(pkg, version);
    const action =
      kind === "rebench-macos" ? canRebenchMacos(state) : primaryAction(state);
    if (!action) return;
    setActionTarget({ pkg, version, state, action });
    setActionReason("");
    setActionError(null);
  }

  async function submitAction() {
    if (!actionTarget) return;
    const { pkg, version, state, action } = actionTarget;
    setActionSending(true);
    setActionError(null);
    try {
      const res = await call<{ ok: true; action?: PipelineActionKind }>(
        `/publish/versions/${encodeURIComponent(pkg)}/${encodeURIComponent(version)}/${action.kind}`,
        {
          method: "POST",
          body:
            action.kind === "rebuild"
              ? JSON.stringify({ reason: actionReason.trim() })
              : undefined,
        },
      );
      const queued = res.action ?? action.kind;
      toast(queuedToast(queued, state.kind, pkg, version));
      setLocalQueued((m) =>
        new Map(m).set(versionKey(pkg, version), {
          pkg,
          version,
          kind: queued,
          at: Date.now(),
          requestedBy: null,
        }),
      );
      setActionTarget(null);
      if (manualPackage === pkg && manualVersion.trim() === version)
        setManualVersion("");
      load();
      loadRunsHistory();
    } catch (err) {
      setActionError(errMsg(err, `failed to queue the ${action.label}`));
    } finally {
      setActionSending(false);
    }
  }

  // Cancel an in-progress attempt — direct operator request, 2026-09-17:
  // "I need a way to cancel it." Takes the full PublishVersion (not just
  // a name/version pair like republish above) since AttemptCard already
  // has the row in hand and there's no "manual cancel of a version this
  // page has no row for" case the way republish's own manual-entry field
  // needs to cover (nothing to cancel if there's no live row for it).
  async function cancelAttempt(v: PublishVersion) {
    if (
      !window.confirm(
        v.status === "pending"
          ? `Drop the queued ${attemptModeLabel(v.run_mode) ?? "attempt"} for ${v.package_name}@${v.version} before it starts? The version goes back to the state it was in.`
          : v.status === "rebenching_macos"
            ? `Cancel the macOS benchmark re-run for ${v.package_name}@${v.version}? The Mac job is stopped and the stored benchmark result stays as it was; the build stays published.`
            : `Cancel the in-progress attempt for ${v.package_name}@${v.version} (compiler ${v.compiler_version ?? "—"})? Any actively-running compile is stopped immediately; this cannot be undone.`,
      )
    )
      return;
    const key = `${v.package_name}:${v.version}:${v.compiler_version ?? ""}`;
    setCancellingKey(key);
    try {
      await call(
        `/publish/versions/${encodeURIComponent(v.package_name)}/${encodeURIComponent(v.version)}/cancel`,
        { method: "POST" },
      );
      toast(`cancel requested for ${v.package_name}@${v.version}`);
      load();
    } catch (err) {
      setError(errMsg(err, "failed to cancel"));
    } finally {
      setCancellingKey(null);
    }
  }

  // Rollback of X.Y.Z to upstream (moving-pointer scheme, 2026-10-12):
  // X.Y.Z stops pointing at our builds and resolves to upstream's original
  // package; every build of it is withdrawn (still installable when
  // pinned). To revert just the newest build, withdraw that build in the
  // builds list instead.
  async function withdraw(v: PublishVersion) {
    const reason = window.prompt(
      `Roll back ${v.package_name}@${v.version} to upstream? New installs of ${v.version} from registry.pure.dev will get upstream's original package, every Pure build of it is marked withdrawn, and automatic runs will not build it again (only Rebuild can).\n\nNothing is deleted: lockfiles that recorded one of our builds keep installing exactly that build.\n\n(To undo only the newest build, withdraw that build in the version's builds list instead.)\n\nReason (required):`,
      "",
    );
    if (reason === null) return;
    if (!reason.trim()) {
      setError("a reason is required to roll back a published version");
      return;
    }
    const key = `withdraw:${v.package_name}:${v.version}`;
    setBusyKey(key);
    try {
      await call(
        `/publish/versions/${encodeURIComponent(v.package_name)}/${encodeURIComponent(v.version)}/withdraw`,
        { method: "POST", body: JSON.stringify({ reason: reason.trim() }) },
      );
      toast(`rollback requested for ${v.package_name}@${v.version}`);
      load();
    } catch (err) {
      setError(errMsg(err, "failed to roll back"));
    } finally {
      setBusyKey(null);
    }
  }

  // Withdraw ONE build: X.Y.Z reverts to the previous live build (or to
  // upstream's original when none is left). Same broker route as Roll
  // back, with the build version.
  async function withdrawBuild(pkg: string, version: string, build: string) {
    const reason = window.prompt(
      `Withdraw build ${pkg}@${build}? It is marked deprecated and never pointed to again; if ${version} points at it now, ${version} reverts to the previous build (or upstream's original if none is left).\n\nNothing is deleted: lockfiles that pinned this build keep installing it.\n\nReason (required):`,
      "",
    );
    if (reason === null) return;
    if (!reason.trim()) {
      setError("a reason is required to withdraw a build");
      return;
    }
    const key = `withdraw:${pkg}:${build}`;
    setBusyKey(key);
    try {
      await call(
        `/publish/versions/${encodeURIComponent(pkg)}/${encodeURIComponent(build)}/withdraw`,
        { method: "POST", body: JSON.stringify({ reason: reason.trim() }) },
      );
      toast(`withdraw requested for ${pkg}@${build}`);
      load();
    } catch (err) {
      setError(errMsg(err, "failed to withdraw the build"));
    } finally {
      setBusyKey(null);
    }
  }

  function openDeprecate(v: PublishVersion) {
    setDeprecateTarget(v);
    setDeprecateMessage(v.deprecated ?? "");
    setDeprecateError(null);
  }

  async function submitDeprecate() {
    if (!deprecateTarget) return;
    setDeprecating(true);
    setDeprecateError(null);
    try {
      await call(
        `/publish/versions/${encodeURIComponent(deprecateTarget.package_name)}/${encodeURIComponent(deprecateTarget.version)}/deprecate`,
        {
          method: "POST",
          body: JSON.stringify({ message: deprecateMessage.trim() }),
        },
      );
      toast(
        deprecateMessage.trim()
          ? `${deprecateTarget.package_name}@${deprecateTarget.version} deprecated`
          : `deprecation cleared for ${deprecateTarget.package_name}@${deprecateTarget.version}`,
      );
      setDeprecateTarget(null);
      load();
    } catch (err) {
      setDeprecateError(errMsg(err, "failed to update deprecation"));
    } finally {
      setDeprecating(false);
    }
  }

  const manualState =
    manualPackage && manualVersion.trim()
      ? stateOf(manualPackage, manualVersion.trim())
      : null;
  const manualAction = manualState ? primaryAction(manualState) : null;
  // The per-version action renders once, on that version's most recently
  // updated row (the table lists one row per compiler version).
  const headRowKeys = new Set<string>();
  {
    const seen = new Set<string>();
    for (const { v } of flattenedVersions) {
      const k = versionKey(v.package_name, v.version);
      if (seen.has(k)) continue;
      seen.add(k);
      headRowKeys.add(
        `${v.package_name}:${v.version}:${v.compiler_version ?? ""}`,
      );
    }
  }

  // The version-level actions for its head row: while an attempt is queued
  // or running, its status and Cancel; otherwise the ONE pipeline action
  // plus Deprecate / Roll back when (and only when) the registry serves a
  // build to act on. Never a button that would be a no-op.
  function renderVersionActions(v: PublishVersion) {
    const state = stateOf(v.package_name, v.version);
    if (state.kind === "in_progress") {
      const a = state.attempt;
      const mode = attemptModeLabel(a.run_mode);
      const cancelKey = `${a.package_name}:${a.version}:${a.compiler_version ?? ""}`;
      return (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {a.status === "pending" ? "queued" : STATUS_LABEL[a.status]}
            {mode ? ` · ${mode}` : ""}
            {a.requested_by ? ` · ${a.requested_by}` : ""}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={a.local_placeholder || cancellingKey === cancelKey}
            title={
              a.local_placeholder
                ? "Cancel becomes available once the pipeline has the request (a few seconds)"
                : a.status === "pending"
                  ? "Drop this queued request before it starts"
                  : "Stop this in-progress attempt — any actively-running compile is cancelled immediately"
            }
            onClick={() => cancelAttempt(a)}
          >
            {cancellingKey === cancelKey ? "…" : "Cancel"}
          </Button>
        </div>
      );
    }
    const action = primaryAction(state);
    const rebench = canRebenchMacos(state);
    return (
      <div className="flex flex-wrap items-center gap-2">
        {action && (
          <Button
            variant="outline"
            size="sm"
            title={action.title}
            onClick={() => requestAction(v.package_name, v.version)}
          >
            {action.label}
          </Button>
        )}
        {rebench && (
          <Button
            variant="outline"
            size="sm"
            title={rebench.title}
            data-testid="rebench-macos"
            onClick={() =>
              requestAction(v.package_name, v.version, "rebench-macos")
            }
          >
            {rebench.label}
          </Button>
        )}
        {canDeprecate(state) && state.kind === "served" && (
          <Button
            variant="outline"
            size="sm"
            title={
              state.deprecated
                ? `Deprecated: “${state.deprecated}” — change or clear the message`
                : "Warn installers of this version (npm deprecate)"
            }
            onClick={() =>
              openDeprecate({ ...v, deprecated: state.deprecated })
            }
          >
            {state.deprecated ? "Edit deprecation" : "Deprecate"}
          </Button>
        )}
        {canRollBack(state) && (
          <Button
            variant="outline"
            size="sm"
            disabled={busyKey === `withdraw:${v.package_name}:${v.version}`}
            title="Roll this version back to upstream's original package (every Pure build of it withdrawn; lockfiles keep the builds they pinned)"
            onClick={() => withdraw(v)}
          >
            Roll back
          </Button>
        )}
      </div>
    );
  }

  const worstPublishState: HealthState | "none" | null =
    publishHealth === null
      ? null
      : publishHealth.length === 0
        ? "none"
        : publishHealth.some((h) => h.state === "down")
          ? "down"
          : publishHealth.some(
                (h) => h.state === "degraded" || h.state === "unknown",
              )
            ? "degraded"
            : "up";

  return (
    <div className="max-w-6xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold">Registry</h1>
            {worstPublishState === "up" && (
              <Badge
                variant="success"
                className="font-normal"
                title="internal/publish is reporting a healthy heartbeat"
              >
                pipeline up
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            Packages the publish pipeline is allowed to compile, test, and
            publish to registry.pure.dev — opt-in only, never automatic
            (internal/publish/plans/2026-09-08-publish-pipeline.md). Adding a
            package here is what makes it eligible for the npm-update watcher
            and compiler-release re-verification sweeps.
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)} className="shrink-0">
          Add package
        </Button>
      </div>

      {worstPublishState !== null && worstPublishState !== "up" && (
        <Alert
          variant={worstPublishState === "down" ? "destructive" : "warning"}
        >
          <AlertTitle>
            {worstPublishState === "none"
              ? "No publish pipeline host found"
              : `Publish pipeline ${worstPublishState}`}
          </AlertTitle>
          <AlertDescription>
            {worstPublishState === "none" ? (
              <>
                internal/publish isn't reporting a liveness heartbeat anywhere
                in the fleet — tracked packages won't be checked or compiled
                until a host comes up.
              </>
            ) : (
              <>
                {publishHealth
                  ?.filter((h) => h.state !== "up")
                  .map((h) => `${h.hostname}: ${h.state}`)
                  .join(", ")}{" "}
                — tracked packages may not be checked or compiled correctly
                until this clears.
              </>
            )}{" "}
            <Link to="/fleet" className="underline">
              View fleet
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}

      {problemAttempts.length > 0 && (
        <Alert variant="warning" data-testid="page-step-banner">
          <AlertTitle>
            Degraded or failed steps in{" "}
            {problemAttempts.length === 1
              ? "a recent publish run"
              : `${problemAttempts.length} recent publish runs`}
          </AlertTitle>
          <AlertDescription>
            <ul className="space-y-1">
              {problemAttempts.map((a) => (
                <li key={a.key}>
                  <span className="font-mono text-xs font-medium">
                    {a.title}
                  </span>
                  <ul className="list-disc pl-4 font-mono text-xs whitespace-pre-wrap break-words">
                    {a.lines.map((l, i) => (
                      <li key={i}>{l}</li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>Packages</CardTitle>
              <CardDescription>
                Enable/disable controls whether the watcher (npm-hook + poll
                backstop) and compiler-release sweeps are permitted to enqueue
                new work for this package — it never deletes anything already
                published.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Input
                placeholder="Filter by name…"
                value={packageFilter}
                onChange={(e) => setPackageFilter(e.target.value)}
                className="h-8 w-48 text-sm"
              />
              <UpdatedAgo cell={lastLoaded} fmt={fmtAgo} />
              <Button variant="outline" size="sm" onClick={load}>
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Package</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Enabled</TableHead>
                <TableHead>Version tracking</TableHead>
                <TableHead>Optimisation</TableHead>
                <TableHead>Added</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPackages.map((pkg) => (
                <TableRow key={pkg.name}>
                  <TableCell className="font-mono text-xs">
                    {pkg.name}
                  </TableCell>
                  <TableCell>
                    <PackageSummaryBadge summary={summarizePackage(pkg)} />
                  </TableCell>
                  <TableCell>
                    <Badge variant={pkg.enabled ? "success" : "secondary"}>
                      {pkg.enabled ? "enabled" : "disabled"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Badge
                        variant={
                          pkg.version_mode === "latest"
                            ? "secondary"
                            : "default"
                        }
                      >
                        {VERSION_MODE_LABEL[pkg.version_mode]}
                      </Badge>
                      {pkg.version_mode === "pattern" && (
                        <span className="font-mono text-xs text-muted-foreground">
                          {pkg.version_pattern}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <PublishToggles
                      name={pkg.name}
                      settings={pkg.settings}
                      busyKey={busyKey}
                      onChange={(key, to) => setPackageSetting(pkg, key, to)}
                    />
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {fmtDate(pkg.added_at)}
                    {pkg.added_by ? ` · ${pkg.added_by}` : ""}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busyKey === `toggle:${pkg.name}`}
                        onClick={() => toggleEnabled(pkg)}
                      >
                        {busyKey === `toggle:${pkg.name}`
                          ? "…"
                          : pkg.enabled
                            ? "Disable"
                            : "Enable"}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        title="Change which upstream npm versions this package tracks"
                        onClick={() => openTracking(pkg)}
                      >
                        Edit tracking
                      </Button>
                      {pkg.versions.length === 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          title="Stop tracking this package. Only available while it has no versions; a package with history can only be disabled."
                          disabled={busyKey === `delete:${pkg.name}`}
                          onClick={() => deletePackage(pkg)}
                        >
                          {busyKey === `delete:${pkg.name}` ? "…" : "Delete"}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {packages?.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No packages tracked yet — add one to start the pipeline.
            </p>
          )}
          {packages !== null &&
            packages.length > 0 &&
            filteredPackages.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No tracked package matches “{packageFilter}”.
              </p>
            )}
          {packages === null && !error && (
            <p className="text-sm text-muted-foreground">Loading…</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Tracked packages</CardTitle>
          <CardDescription>
            One row per tracked (package, version, compiler version) — 20
            versions across 2 compiler versions is 40 rows here. "Current
            status" is the plain published/unpublished fact; "Compiler status"
            is where that build actually is right now.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-2 mb-4 pb-4 border-b">
            <div className="space-y-1">
              <Label htmlFor="manual-republish-package" className="text-xs">
                Package
              </Label>
              <Select
                id="manual-republish-package"
                className="w-48 h-8 text-sm"
                value={manualPackage}
                onChange={(e) => setManualPackage(e.target.value)}
              >
                <option value="">(choose a tracked package)</option>
                {packages?.map((pkg) => (
                  <option key={pkg.name} value={pkg.name}>
                    {pkg.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="manual-republish-version" className="text-xs">
                Version
              </Label>
              <Input
                id="manual-republish-version"
                placeholder="4.18.1"
                value={manualVersion}
                onChange={(e) => setManualVersion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && manualAction)
                    requestAction(manualPackage, manualVersion.trim());
                }}
                className="font-mono w-40 h-8 text-sm"
              />
            </div>
            {manualState?.kind === "in_progress" ? (
              <span className="text-xs text-muted-foreground pb-1.5">
                {manualPackage}@{manualVersion.trim()} already has an attempt
                queued or in progress — see its row below.
              </span>
            ) : (
              <Button
                variant="outline"
                size="sm"
                disabled={!manualAction}
                onClick={() =>
                  requestAction(manualPackage, manualVersion.trim())
                }
                title={
                  manualAction
                    ? `${manualAction.title}. Works for a version this page has no row for yet — e.g. one that ran and failed before this page's own event relay existed.`
                    : "Choose a package and enter a version"
                }
              >
                {manualAction?.label ?? "Publish"}
              </Button>
            )}
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Package</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Compiler</TableHead>
                <TableHead>Current status</TableHead>
                <TableHead>Compiler status</TableHead>
                <TableHead>Last published</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {flattenedVersions.map(({ v }) => {
                const rowKey = `${v.package_name}:${v.version}:${v.compiler_version ?? ""}`;
                const bucket = COMPILER_STATUS_BUCKET[v.status];
                return (
                  <Fragment key={rowKey}>
                    <TableRow>
                      <TableCell className="font-mono text-xs">
                        {v.package_name}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {v.version}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {v.compiler_version ?? "—"}
                      </TableCell>
                      <TableCell>
                        <BuildStatusBadge v={v} />
                        {v.deprecated && (
                          <Badge variant="warning" className="ml-1">
                            deprecated
                          </Badge>
                        )}
                        <PgoBadge v={v} className="ml-1" />
                        <TestsAfterPublishBadge v={v} className="ml-1" />
                        <MacosBadge v={v} className="ml-1" />
                        <MacosVerifyBadge v={v} className="ml-1" />
                        <MacosBenchBadge v={v} className="ml-1" />
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={COMPILER_STATUS_VARIANT[bucket]}
                          className={
                            IN_PROGRESS_STATUSES.has(v.status)
                              ? "animate-pulse"
                              : undefined
                          }
                          // Coarse bucket by design (this table is scanned
                          // across many rows at once — see this file's
                          // header) but the hover reveals the real granular
                          // step, same STATUS_LABEL "Current queue" uses.
                          title={STATUS_LABEL[v.status]}
                        >
                          {bucket}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {v.status === "published" ? fmtDate(v.updated_at) : "—"}
                      </TableCell>
                      <TableCell>
                        {headRowKeys.has(rowKey) ? (
                          renderVersionActions(v)
                        ) : (
                          <span
                            className="text-xs text-muted-foreground"
                            title="Actions apply to the whole version and are on its most recent row above"
                          >
                            —
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                    {headRowKeys.has(rowKey) &&
                      (() => {
                        const view = registryBuilds.get(v.package_name);
                        const err = view && "error" in view ? view.error : null;
                        const builds =
                          view && !("error" in view)
                            ? view.versions[v.version]
                            : undefined;
                        if (!err && (!builds || builds.builds.length === 0))
                          return null;
                        return (
                          <TableRow
                            key={`${rowKey}-builds`}
                            className="hover:bg-transparent"
                          >
                            <TableCell colSpan={7} className="pt-0 pb-3">
                              <VersionBuilds
                                pkg={v.package_name}
                                version={v.version}
                                builds={builds}
                                error={err}
                                busyBuild={
                                  busyKey?.startsWith(
                                    `withdraw:${v.package_name}:`,
                                  )
                                    ? busyKey.slice(
                                        `withdraw:${v.package_name}:`.length,
                                      )
                                    : null
                                }
                                onWithdrawBuild={(b) =>
                                  withdrawBuild(v.package_name, v.version, b)
                                }
                                downloads={downloads.get(v.package_name)}
                                selected={
                                  deleteSelection[
                                    `${v.package_name}@${v.version}`
                                  ] ?? []
                                }
                                onToggleSelected={(b) =>
                                  setDeleteSelection((s) => {
                                    const k = `${v.package_name}@${v.version}`;
                                    return {
                                      ...s,
                                      [k]: toggleSelected(s[k] ?? [], b),
                                    };
                                  })
                                }
                                onSelectWithdrawn={() =>
                                  setDeleteSelection((s) => ({
                                    ...s,
                                    [`${v.package_name}@${v.version}`]:
                                      selectWithdrawn(builds?.builds ?? []),
                                  }))
                                }
                                onDeleteBuilds={(versions) =>
                                  setDeleteTarget({
                                    pkg: v.package_name,
                                    versions,
                                  })
                                }
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })()}
                    {v.status === "failed" && v.last_error && (
                      <TableRow
                        key={`${rowKey}-error`}
                        className="hover:bg-transparent"
                      >
                        <TableCell colSpan={7} className="pt-0 pb-3">
                          <Alert variant="destructive" className="py-2">
                            <AlertDescription className="font-mono text-xs whitespace-pre-wrap break-words">
                              {v.last_error}
                            </AlertDescription>
                          </Alert>
                        </TableCell>
                      </TableRow>
                    )}
                    {hasPgoDegradedDetail(v) && (
                      <TableRow
                        key={`${rowKey}-pgo`}
                        className="hover:bg-transparent"
                      >
                        <TableCell colSpan={7} className="pt-0 pb-3">
                          <PgoDegradedPanel v={v} />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
          {flattenedVersions.length === 0 && (
            <p className="text-sm text-muted-foreground">
              {filteredPackages.length === 0
                ? "No tracked packages yet — add one above to start the pipeline."
                : "No versions detected yet for any matching package — the watcher checks npm on its next scheduled poll (or immediately, the moment tracking was added/changed). Already know a version was attempted (e.g. from the box's own logs)? State-change events only ever reach this page the moment a pipeline actually runs — trigger a fresh attempt above instead of waiting for one that already happened."}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <CardTitle>Current queue</CardTitle>
              <CardDescription>
                One row per pipeline attempt (package, version, compiler
                version), most-recently-updated first — the status itself names
                whichever step is currently running.
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Label htmlFor="queue-package-filter" className="text-xs">
                Filter by package
              </Label>
              <Select
                id="queue-package-filter"
                className="w-56 h-8 text-sm"
                value={queuePackageFilter}
                onChange={(e) => setQueuePackageFilter(e.target.value)}
              >
                <option value="">(all tracked packages)</option>
                {packages?.map((pkg) => (
                  <option key={pkg.name} value={pkg.name}>
                    {pkg.name}
                  </option>
                ))}
              </Select>
              <Button variant="outline" size="sm" onClick={load}>
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {recentAttempts.slice(0, queueShown).map(({ v, historical }) => (
            <AttemptCard
              // STABLE per attempt: an update to a live attempt must keep the
              // same component (its open detail drawer, scroll and focus).
              // Only a past attempt, which never changes, needs updated_at to
              // tell it from the live one of the same identity.
              key={
                historical
                  ? `h:${v.package_name}:${v.version}:${v.compiler_version ?? ""}:${v.updated_at}`
                  : `c:${v.package_name}:${v.version}:${v.compiler_version ?? ""}`
              }
              v={v}
              historical={historical}
              currentSettings={
                packages?.find((p) => p.name === v.package_name)?.settings
              }
              onCancel={historical ? undefined : () => cancelAttempt(v)}
              cancelling={
                cancellingKey ===
                `${v.package_name}:${v.version}:${v.compiler_version ?? ""}`
              }
            />
          ))}
          {recentAttempts.length > queueShown && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setQueueShown((n) => n + 10)}
            >
              Show more ({recentAttempts.length - queueShown} older)
            </Button>
          )}
          {recentAttempts.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No attempts recorded yet
              {queuePackageFilter ? ` for ${queuePackageFilter}` : ""}.
            </p>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={actionTarget !== null}
        onOpenChange={(o) => !o && !actionSending && setActionTarget(null)}
      >
        <DialogContent>
          {actionTarget &&
            (() => {
              const { pkg, version, state, action } = actionTarget;
              const text =
                action.kind === "rebench-macos"
                  ? rebenchConfirmText(pkg, version)
                  : confirmText(state, pkg, version);
              return (
                <>
                  <DialogHeader>
                    <DialogTitle>{text.title}</DialogTitle>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      {text.body.map((line) => (
                        <p key={line}>{line}</p>
                      ))}
                    </div>
                  </DialogHeader>
                  {action?.kind === "rebuild" && (
                    <div className="space-y-2">
                      <Label htmlFor="action-reason">Reason (optional)</Label>
                      <Input
                        id="action-reason"
                        placeholder="e.g. compiler fix for the arm64 crash"
                        value={actionReason}
                        onChange={(e) => setActionReason(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") void submitAction();
                        }}
                        autoFocus
                      />
                    </div>
                  )}
                  {actionError && (
                    <p className="text-sm text-destructive">{actionError}</p>
                  )}
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setActionTarget(null)}
                      disabled={actionSending}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={submitAction}
                      disabled={actionSending || !action}
                    >
                      {actionSending ? "Queuing…" : (action?.label ?? "")}
                    </Button>
                  </DialogFooter>
                </>
              );
            })()}
        </DialogContent>
      </Dialog>

      <DeleteBuildsDialog
        pkg={deleteTarget?.pkg ?? ""}
        versions={deleteTarget?.versions ?? []}
        onClose={() => setDeleteTarget(null)}
        onDone={() => {
          setDeleteSelection({});
          load();
        }}
      />

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add package</DialogTitle>
            <DialogDescription>
              Adds a package to the tracked allowlist — the watcher only ever
              watches versions of packages already on this list, and nothing
              here builds a package the operator hasn't explicitly added.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="add-package-name">Package name</Label>
              <Input
                id="add-package-name"
                placeholder="chalk"
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && addVersionMode !== "pattern")
                    void submitAdd();
                }}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-version-mode">Version tracking</Label>
              <Select
                id="add-version-mode"
                value={addVersionMode}
                onChange={(e) =>
                  setAddVersionMode(e.target.value as VersionTrackingMode)
                }
              >
                <option value="latest">Latest version only (default)</option>
                <option value="all">All versions</option>
                <option value="pattern">Versions matching a pattern…</option>
              </Select>
              <p className="text-xs text-muted-foreground">
                {addVersionMode === "latest" &&
                  "Only npm's current “latest” dist-tag is ever tracked, going forward."}
                {addVersionMode === "all" &&
                  "Every version npm has ever published for this package — including its full existing history. For a package with a long release history this can enqueue many compiles at once."}
                {addVersionMode === "pattern" &&
                  "Every version — past or future — matching the range below."}
              </p>
            </div>
            {addVersionMode === "pattern" && (
              <div className="space-y-2">
                <Label htmlFor="add-version-pattern">
                  Version pattern (node-semver range)
                </Label>
                <Input
                  id="add-version-pattern"
                  placeholder="^4.0.0"
                  value={addVersionPattern}
                  onChange={(e) => setAddVersionPattern(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void submitAdd();
                  }}
                  className="font-mono"
                />
                <p className="text-xs text-muted-foreground">
                  Examples: <code className="font-mono">^4.0.0</code>,{" "}
                  <code className="font-mono">4.x</code>,{" "}
                  <code className="font-mono">&gt;=4.0.0 &lt;5.0.0</code>. A
                  glob or npm dist-tag name (e.g.{" "}
                  <code className="font-mono">next</code>) will not match.
                </p>
              </div>
            )}
            {addError && <p className="text-sm text-destructive">{addError}</p>}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAddOpen(false)}
              disabled={adding}
            >
              Cancel
            </Button>
            <Button onClick={submitAdd} disabled={adding}>
              {adding ? "Adding…" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={trackingTarget !== null}
        onOpenChange={(o) => !o && setTrackingTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Version tracking —{" "}
              <span className="font-mono">{trackingTarget?.name}</span>
            </DialogTitle>
            <DialogDescription>
              Changes take effect immediately — npm is checked right away for
              whatever the new mode newly covers, not just on the next scheduled
              poll.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-version-mode">Version tracking</Label>
              <Select
                id="edit-version-mode"
                value={trackingMode}
                onChange={(e) =>
                  setTrackingMode(e.target.value as VersionTrackingMode)
                }
              >
                <option value="latest">Latest version only (default)</option>
                <option value="all">All versions</option>
                <option value="pattern">Versions matching a pattern…</option>
              </Select>
            </div>
            {trackingMode === "pattern" && (
              <div className="space-y-2">
                <Label htmlFor="edit-version-pattern">
                  Version pattern (node-semver range)
                </Label>
                <Input
                  id="edit-version-pattern"
                  placeholder="^4.0.0"
                  value={trackingPattern}
                  onChange={(e) => setTrackingPattern(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void submitTracking();
                  }}
                  className="font-mono"
                  autoFocus
                />
              </div>
            )}
            {trackingError && (
              <p className="text-sm text-destructive">{trackingError}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTrackingTarget(null)}
              disabled={trackingSaving}
            >
              Cancel
            </Button>
            <Button onClick={submitTracking} disabled={trackingSaving}>
              {trackingSaving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deprecateTarget !== null}
        onOpenChange={(o) => !o && setDeprecateTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {deprecateTarget?.deprecated ? "Edit deprecation" : "Deprecate"}{" "}
              <span className="font-mono">
                {deprecateTarget?.package_name}@{deprecateTarget?.version}
              </span>
            </DialogTitle>
            <DialogDescription>
              Sets a mutable warning message on this already-published version —
              the npm-registry-protocol equivalent of{" "}
              <code className="bg-muted px-1 py-0.5 rounded">
                npm deprecate
              </code>
              . This does not remove or alter the published tarball (versions
              are immutable — plan §6); it only warns installers. Leave the
              message empty to clear an existing deprecation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="deprecate-message">Deprecation message</Label>
            <Input
              id="deprecate-message"
              placeholder="Critical bug — use 8.17.1 instead"
              value={deprecateMessage}
              onChange={(e) => setDeprecateMessage(e.target.value)}
              autoFocus
            />
            {deprecateError && (
              <p className="text-sm text-destructive">{deprecateError}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeprecateTarget(null)}
              disabled={deprecating}
            >
              Cancel
            </Button>
            <Button
              variant={deprecateMessage.trim() ? "destructive" : "default"}
              onClick={submitDeprecate}
              disabled={
                deprecating ||
                !deprecationChanges(
                  deprecateTarget?.deprecated ?? null,
                  deprecateMessage,
                )
              }
              title={
                deprecationChanges(
                  deprecateTarget?.deprecated ?? null,
                  deprecateMessage,
                )
                  ? undefined
                  : deprecateTarget?.deprecated
                    ? "Change the message, or clear it to remove the deprecation"
                    : "Enter a deprecation message"
              }
            >
              {deprecating
                ? "Saving…"
                : deprecateMessage.trim()
                  ? "Deprecate"
                  : "Clear deprecation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
